/* eslint-disable */
// @ts-ignore
import * as d3 from 'd3';
// @ts-ignore
import { geoRobinson } from 'd3-geo-projection';
declare var vca: any;

interface IGeoJsonObject {
    type: string;
    features: Array<any>;
}

interface IRegion {
    regionCode: string;
    name: string;
    color: IColor;
}

interface IColor {
    dark: string;
    light: string;
}

const ColorPalette: IColor[] = [
    { dark: "#68AAB9", light: "#C6DFE5" },
    { dark: "#C7B697", light: "#DCD5C8" },
    { dark: "#B59CC9", light: "#D5C9DF" },
    { dark: "#A0BDA3", light: "#CBD9CF" },
    { dark: "#EE9494", light: "#F1C4C4" },
    { dark: "#D6CF8F", light: "#E5E2C2" },
    { dark: "#85A7DB", light: "#BDCEE7" },
    { dark: "#EDC78D", light: "#F1DEC1" },
    { dark: "#D699D8", light: "#E5C7E6" },
    { dark: "#B2C779", light: "#D4DEB6" },
    { dark: "#E4A389", light: "#EDCCBF" },
    { dark: "#9DA5C1", light: "#CED2E0" },
];

const ENTITY_HIGHLIGHT_BACKGROUND = "#FFD500";

interface ICountry {
    countryCode: string;
    regionCode: string;
    name: string;
}

interface IEntityDto {
    entityId: string;
    regionCode: string;
    countryCode: string;
    entityTypes: IEntityType[];
    isHighlighted: boolean;
}

interface IEntityType {
    name: string;
}

type HighlightFilterMode = 'All' | 'Highlighted' | 'NotHighlighted';

interface IFunctionTooltip {
    countryCode: string;
    name: string;
    text: string;
    function: string;
    total: number | null;
    displayValue: string | null;
    allFunctions: string[] | null;
}

interface IEntityIcon {
    id: number;
    regionCode: string;
    countryCode: string;
    entityType: IEntityType;
    entityId: string;
    visible: boolean;
}

enum MapMode {
    Entities,
    Flows
}

interface ICell {
    id: string,
}

interface IElement extends ICell {
    entityIds: string[]
}

interface ILink extends ICell {
    type: string,
    source: ICell,
    target: ICell,
    color: string,
    dashed: boolean,
}

interface IFlowArrow {
    type: string,
    sourceId: string,
    targetId: string,
    color: string,
    dashed: boolean,
}

// TODO: consolidate this FlowType and flowTypeStyles with the ones in flows.ts into a single shared resource
enum FlowType {
    Physical,
    Legal,
    Payment,
    Service,
    IPTransfer,
    IPLicense,
    Agreement,
    Miscellaneous,
    Ownership,
}

const flowTypeStyles: readonly {type: string, color: string, dashed: boolean}[] = [
    { type: 'PhysicalLink', color: '#CE1210', dashed: false },
    { type: 'LegalLink', color: '#99C13C', dashed: true },
    { type: 'PaymentLink', color: '#9B9B9D', dashed: true },
    { type: 'ServiceLink', color: 'black', dashed: false },
    { type: 'IPTransferLink', color: '#FFA500', dashed: false },
    { type: 'IPLicenseLink', color: '#8E25AB', dashed: false },
    { type: 'AgreementLink', color: '#256F8C', dashed: false },
    { type: 'OwnershipLink', color: '#000000', dashed: false },
    { type: 'MiscellaneousLink', color: '#EB8100', dashed: false },
];

interface IFutureStateStepSummary {
    $type: string;
    stepSummaryId: string;
    title: string;
    details: string;
}

interface ISingleEntityStepSummary extends IFutureStateStepSummary {
    countryCode: string;
    entityId: string;
}

interface IAddTaxRateStepSummary extends ISingleEntityStepSummary {
}

function isAddTaxRateStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IAddTaxRateStepSummary {
    return stepSummary.$type === "addTaxRateStepSummary";
}

interface IEditTaxRateStepSummary extends ISingleEntityStepSummary {
}

function isEditTaxRateStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IEditTaxRateStepSummary {
    return stepSummary.$type === "editTaxRateStepSummary";
}

interface IEditNolUtilizationRateStepSummary extends ISingleEntityStepSummary {
}

function isEditNolUtilizationRateStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IEditNolUtilizationRateStepSummary {
    return stepSummary.$type === "editNolUtilizationRateStepSummary";
}

interface IAddEntityStepSummary extends ISingleEntityStepSummary {
}

function isAddEntityStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IAddEntityStepSummary {
    return stepSummary.$type === "addEntityStepSummary";
}

interface IUpdateEntityTypesStep extends ISingleEntityStepSummary {
}

function isUpdateEntityTypesStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IUpdateEntityTypesStep {
    return stepSummary.$type === "updateEntityTypesStepSummary";
}

interface IUpdateEntityTagsStep extends ISingleEntityStepSummary {
}

function isUpdateEntityTagsStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IUpdateEntityTagsStep {
    return stepSummary.$type === "updateEntityTagsStepSummary";
}

interface IIpOwnerSummary extends ISingleEntityStepSummary {
}

function isIpOwnerSummary(stepSummary: IFutureStateStepSummary): stepSummary is IIpOwnerSummary {
    return stepSummary.$type === "ipOwnerSummary";
}

interface IServiceProviderSummary extends ISingleEntityStepSummary {
}

function isServiceProviderSummary(stepSummary: IFutureStateStepSummary): stepSummary is IServiceProviderSummary {
    return stepSummary.$type === "serviceProviderSummary";
}

interface IUpdateLineItemsStepSummary extends ISingleEntityStepSummary {}

function isUpdateLineItemsStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IUpdateLineItemsStepSummary {
    return stepSummary.$type === "updateLineItemsStepSummary";
}

// Interest Limitation (single-entity) summary
interface IEditInterestLimitationStepSummary extends ISingleEntityStepSummary {}

function isEditInterestLimitationStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IEditInterestLimitationStepSummary {
    return stepSummary.$type === "editInterestLimitationStepSummary";
}

interface IEntityRelationshipStepSummary extends IFutureStateStepSummary {
    sourceCountryCodes: string[];
    targetCountryCodes: string[];
    sourceEntityIds: string[];
    targetEntityIds: string[];
}

function isEntityRelationshipStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IEntityRelationshipStepSummary {
    return stepSummary.$type === "addRelationshipStepSummary" || stepSummary.$type === "removeRelationshipStepSummary";
}

interface IAddRelationshipStepSummary extends IEntityRelationshipStepSummary {
    flowType: FlowType;
}

function isAddRelationshipStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IAddRelationshipStepSummary {
    return stepSummary.$type === "addRelationshipStepSummary";
}

interface IAddFunctionStepSummary extends IFutureStateStepSummary {
    functionName: string;
    countryCode: string;
    entityId: string;
}
function isAddFunctionStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IAddFunctionStepSummary {
    return stepSummary.$type === "addFunctionStepSummary";
}

interface IMoveFunctionStepSummary extends IFutureStateStepSummary {
    functionMoveRelationships: IFunctionMoveRelationship[];
}
function isMoveFunctionStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IMoveFunctionStepSummary {
    return stepSummary.$type === "moveFunctionStepSummary";
}

interface IAdjustFeeStepSummary extends IFutureStateStepSummary {
    sourceCountryCode: string;
    targetCountryCode: string;
    sourceEntityId: string;
    targetEntityId: string;
}
function isAdjustFeeStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IAdjustFeeStepSummary {
    return stepSummary.$type === "adjustServiceFeeStepSummary" || stepSummary.$type === "adjustRoyaltyFeeStepSummary";
}

interface IIPTransferStepSummary extends IFutureStateStepSummary {
    sourceCountryCode: string;
    targetCountryCode: string;
    sourceEntityId: string;
    targetEntityId: string;
}
function isIPTransferStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IIPTransferStepSummary {
    return stepSummary.$type === "ipTransferStepSummary";
}

interface IBuySellPrincipalStep {
    pocTradeCoEntityId: string;
    pocTradeCoCountryCode: string;
}

function isBuySellPrincipalStep(stepSummary: any): stepSummary is IBuySellPrincipalStep {
    return stepSummary.$type === "buySellPrincipalSummary";
}

interface IBuySellSuppliersStep {
    pocTradeCoEntityId: string;
    pocTradeCoCountryCode: string;
    supplierEntityIds: string[];
    supplierCountryCodes: string[];
    supplierReturnLabels: any;
}

function isBuySellSuppliersStep(stepSummary: any): stepSummary is IBuySellSuppliersStep {
    return stepSummary.$type === "buySellSuppliersSummary";
}

interface IBuySellDistributorsStep {
    pocTradeCoEntityId: string;
    pocTradeCoCountryCode: string;
    distributorEntityIds: string[];
    distributorCountryCodes: string[];
    distributorReturnLabels: any;
}

function isBuySellDistributorsStep(stepSummary: any): stepSummary is IBuySellDistributorsStep {
    return stepSummary.$type === "buySellDistributorsSummary";
}

interface IBuySellSummaryStep {
    supplierCountryCodes: string[];
    distributorCountryCodes: string[];
    pocTradeCoCountryCode: string;
    supplierEntityIds: string[];
    distributorEntityIds: string[];
    pocTradeCoEntityId: string;
}

function isBuySellSummary(stepSummary: any): stepSummary is IBuySellSummaryStep {
    return stepSummary.$type === "buySellSummary";
}

interface IMoveRevenueExpenseStepSummary extends IFutureStateStepSummary {
    sourceEntityIds: string[];
    targetEntityIds: string[];
}

function isMoveRevenueExpenseStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IMoveRevenueExpenseStepSummary {
    return stepSummary.$type === "moveRevenueExpenseStepSummary";
}

interface IMoveRevenueExpenseDetailStepSummary extends IFutureStateStepSummary {
    sourceEntityId: string;
    targetEntityId: string;
}

function isMoveRevenueExpenseDetailStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is IMoveRevenueExpenseDetailStepSummary {
    return stepSummary.$type === "moveRevenueExpenseDetailSummary";
}

interface IMoveExpenseOrRevenueStep {
    sourceCountryCode: string;
    targetCountryCode: string;
    sourceEntityId: string;
    targetEntityId: string;
}

function isMoveExpenseOrRevenueStep(stepSummary: any) : stepSummary is IMoveExpenseOrRevenueStep {
    return stepSummary.$type === "transferRevenueStepSummary" || stepSummary.$type === "transferExpensesStepSummary";
}

interface ICustomsStepSummary extends IFutureStateStepSummary {
    sourceCountryCode: string;
    targetCountryCode: string;
    sourceEntityId: string;
    targetEntityId: string;
}

function isCustomsStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is ICustomsStepSummary {
    return stepSummary.$type === "customsStepSummary";
}

// Loan step summary (single-entity i.e. the lender)
interface ILoanStepSummary extends ISingleEntityStepSummary {
    entityId: string;
}

function isLoanLenderSummary(stepSummary: IFutureStateStepSummary): stepSummary is ILoanStepSummary {
    return stepSummary.$type === "loanLenderSummary";
}

// Loan borrower summary (payment from borrower to lender)
interface ILoanBorrowerStepSummary extends IFutureStateStepSummary {
    lenderEntityId: string;
    borrowerEntityId: string;
}

function isLoanBorrowerStepSummary(stepSummary: IFutureStateStepSummary): stepSummary is ILoanBorrowerStepSummary {
    return stepSummary.$type === "loanBorrowerSummary";
}

interface IFunctionMoveRelationship {
    sourceCountryCode: string,
    targetCountryCode: string,
    sourceEntityId: string;
    targetEntityId: string;
    functionName: string;
}

function flowTypeToLinkClassName(flowType: FlowType){
    switch (flowType) {
        case FlowType.Physical:
            return "PhysicalLink";
        case FlowType.Legal:
            return "LegalLink";
        case FlowType.Payment:
            return "PaymentLink";
        case FlowType.Service:
            return "ServiceLink";
        case FlowType.IPTransfer:
            return "IPTransferLink";
        case FlowType.IPLicense:
            return "IPLicenseLink";
        case FlowType.Agreement:
            return "AgreementLink";
        case FlowType.Ownership:
            return "OwnershipLink";
        case FlowType.Miscellaneous:
            return "MiscellaneousLink";
    }
}

class CurrentStateMap {
    private geoJson: IGeoJsonObject;
    private currentRegionCountries: any[] | undefined;
    private currentZoomMargin: boolean | undefined;
    private mapNodeName: string;
    private mapContainer: HTMLElement;
    private leftMarginPercent: number;
    private topMarginPercent: number;
    private rightMarginPercent: number;
    private bottomMarginPercent: number;
    private dotNetObjectReference: any;
    private svg: any;
    private d3zoom: any;
    private mainGroup: any;
    private regionsGroup: any;
    private iconsGroup: any;
    private telestrationGroup: any;
    private telestrationSurface: any;
    private telestrationStrokeWidth: number = 3;
    private telestrationColor: string = '#FF0000';
    private telestrationGroupsByFutureStateId: Map<number, any> = new Map();
    private entityTypeIconMarkupCache: Map<string, string> = new Map();
    private projection: any;
    private pathGenerator: any;
    private regions: IRegion[];
    private countries: Map<string, ICountry[]>; // regionCode => Countries
    private allEntities: Map<string, IEntityDto[]>; // countryCode => Entities
    private filteredEntities: Map<string, IEntityDto[]> = new Map<string, IEntityDto[]>; // countryCode => Entities
    private selectedFutureStateId: number | null = null;
    private selectedRegionCode: string;
    private selectedCountryCode: string;
    private flowElements: IElement[] = [];
    private flowLinks: ILink[] = [];
    private filteredFlowLinks: ILink[] = [];
    private flowTypesToHide: string[] = [];
    private mapMode: MapMode = MapMode.Entities;
    private currentScale: number;
    private currentRegionTransform: any;
    private panX: number;
    private panY: number;
    private highlightedEntityIds: string[] = [];
    private highlightFilterMode: HighlightFilterMode;
    private rowHoverEntityId: string;
    private countriesForVisualization: string[] = [];
    private minScale: number;
    private maxScale: number;
    private isDragging: boolean;
    private dragged: boolean;
    private mapIcons: IEntityIcon[];
    private heroAnimationRunning: boolean = false;
    private heroAnimationTimeouts: NodeJS.Timeout[] = [];
    private isVisualizing: boolean;
    private visualizationEntityIds: string[];
    private visualizationCaptions: any[];
    private isTransitioning: boolean;
    private isTelestrating: boolean;
    private mutationObserver: MutationObserver;
    private entityPopupResizeObserver: ResizeObserver;
    private entityPopupIconId?: string;
    private selectedStepSummary: IFutureStateStepSummary;
    private isEraserMode: boolean = false;
    private prevEraserPoint: SVGPoint | null = null;

    constructor(mapNodeName: string, maxScale: number, marginLeftPercent: number, marginTopPercent: number, marginRightPercent: number, marginBottomPercent: number, dotNetObjectReference: any) {
        this.mapNodeName = mapNodeName;
        this.currentScale = 1;
        this.minScale = .33;
        this.maxScale = maxScale;
        this.leftMarginPercent = marginLeftPercent;
        this.topMarginPercent = marginTopPercent;
        this.rightMarginPercent = marginRightPercent;
        this.bottomMarginPercent = marginBottomPercent;
        this.dotNetObjectReference = dotNetObjectReference;
        this.highlightFilterMode = 'All';
        this.mapIcons = [];
        this.mutationObserver = new MutationObserver((records, observer) => {
            this.updateFlowNodesAndLines();
            this.updateEntityPopupPosition();

            // some elements are updated differently and need the new X, Y of the observed element
            // const boundingClientRect = this.svg?.node().getBoundingClientRect();
            // if (!boundingClientRect) {
            //     return;
            // }
            // const currentX = boundingClientRect.left;
            // const currentY = boundingClientRect.top;
            // if ((this.mutationObserverX !== undefined || this.mutationObserverY !== undefined)
            //     && (this.mutationObserverX !== currentX || this.mutationObserverY !== currentY)) {
            //     this.updateFlowDetailsPopupPosition(currentX - this.mutationObserverX, currentY - this.mutationObserverY);
            // }
            // // update the values
            // this.mutationObserverX = currentX;
            // this.mutationObserverY = currentY;
        });
        this.entityPopupResizeObserver = new ResizeObserver((entries) => {
            if (this.isTransitioning) {
                return;
            }
            entries.forEach(entry => {
                //console.debug('entityPopupResizeObserver.onresize for:', entry.target.id);
                this.updateEntityPopupPosition();
            });
        });
    }

    public async initialize(regions: IRegion[], countries: ICountry[]): Promise<boolean> {
        try {
            this.geoJson = await d3.json("/assets/ne_110m_admin_0_countries.geojson");
        } catch (error) {
            console.error('Failed to load geoJson data.', error);
            throw error;
        }

        this.mapContainer = document.getElementById(this.mapNodeName);
        if (!this.mapContainer) {
            console.error('Map container not found.');
            return;
        }

        this.projection = geoRobinson().fitSize([this.mapContainer.parentElement.offsetWidth, this.mapContainer.parentElement.offsetHeight], this.geoJson);
        this.pathGenerator = d3.geoPath(this.projection);

        this.regions = [];
        regions.forEach(r => {
            r.color = ColorPalette[this.regions.length % ColorPalette.length];
            this.regions.push(r);
        });

        this.countries = new Map<string, ICountry[]>();
        countries.forEach(c => {
            this.countries.get(c.regionCode)?.push(c) || this.countries.set(c.regionCode, [c]);
        });
        
        this.d3zoom = d3.zoom()
            .scaleExtent([this.minScale, this.maxScale])
            .on("zoom", (event) => {
                //console.debug('d3.zoom.onzoom');// -> event.transform:', event.transform);
                const transform = event.transform;
                this.currentScale = transform.k;
                this.panX = transform.x;
                this.panY = transform.y;
                this.mainGroup.attr("transform", `translate(${this.panX}, ${this.panY}) scale(${this.currentScale})`);
                
                if (event.sourceEvent) {// && event.sourceEvent.type === 'wheel') {
                    // if this is a wheel event, we want to update the icon size
                    this.drawIcons();
                }
            });

        return true;
    }
    
    private onClick() {
        if (this.isTransitioning || this.isTelestrating) {
            return;
        }
        //console.debug('d3.onclick');
        // when clicking anywhere on the map, hide flow detail popup and entity icon popup
        this.hidePopups();
    }

    public beginTelestration() {
        //console.debug("begin telestration");
        this.isTelestrating = true;
        this.hidePopups();
        this.disableRegionalMouseEvents();
        this.disableCountryMouseEvents();
        this.disablePanAndZoom();
        

        // append a new transparent rect to cover the whole screen overlaying the
        this.telestrationSurface = this.svg.append("rect")
            .attr("width", this.mapContainer.clientWidth)
            .attr("height", this.mapContainer.clientHeight)
            .attr("fill", "transparent")
            .attr("pointer-events", "all")
            .attr("border", "1px solid #000")
            .style("cursor", "crosshair")
            .call(d3.drag().container(() => {
                return this.mapContainer;
            }).subject((event) => {
                const svgPt = this.svgPoint(event, this.svg.node(), this.mainGroup.node());
                const p = [svgPt.x, svgPt.y];
                return [p, p];
            }).on("start", (event) => {
                this.telestrationDragStarted(event);
            }));
    }
    
    public setTelestrationColor(color: string) {
        //console.debug("set telestration color to ", color);
        this.telestrationColor = color;
    }
    
    public async endTelestration() {
        //console.debug("end telestration");
        this.isTelestrating = false;
        // remove the telestration surface
        this.telestrationSurface.remove();
        
        await this.afterTransition();
    }
    
    public clearTelestration() {
        //console.debug("clear telestration");
        // remove all telestration paths
        this.telestrationGroup.selectAll(".telestration-stroke").remove();
    }

    private telestrationDragStarted(event) {
        if (this.isEraserMode) {
            const startPt = this.svgPoint(event, this.svg.node(), this.mainGroup.node());
            this.eraseAtPoint(startPt);
            this.prevEraserPoint = startPt;
            event.on("drag", (event) => {
                const svgPt = this.svgPoint(event, this.svg.node(), this.mainGroup.node());
                this.eraseAlongSegment(this.prevEraserPoint, svgPt);
                this.prevEraserPoint = svgPt;
            }).on("end", () => {
                this.prevEraserPoint = null;
            });
            return;
        }

        const line = d3
            .line()
            .curve(d3.curveBasis);

        const d = event.subject;
        const active = this.telestrationGroup
            .append("path")
            .attr("fill", "none")
            .attr("stroke", this.telestrationColor)
            .attr("stroke-width", this.telestrationStrokeWidth / this.currentScale)
            .attr("stroke-linecap", "round")
            .classed("telestration-stroke", true)
            .datum(d);
        let svgPt = this.svgPoint(event, this.svg.node(), this.mainGroup.node());

        event.on("drag", (event) => {
            svgPt = this.svgPoint(event, this.svg.node(), this.mainGroup.node());

            d.push([svgPt.x, svgPt.y]);
            active.attr("d", line);
        });
    }

    public setTelestrationEraserMode(enabled: boolean) {
        this.isEraserMode = enabled;
        // update cursor on telestration surface based on eraser mode
        const cursor = this.isEraserMode ? `url(/icons/eraser_cursor.svg) 8 8, crosshair` : "crosshair";
        if (this.telestrationSurface) {
            this.telestrationSurface.style("cursor", cursor);
        }
    }

    private eraseAtPoint(svgPoint: SVGPoint) {
        // iterate telestration paths and remove the first one that intersects the point
        const domPt = new DOMPoint(svgPoint.x, svgPoint.y);
        const paths = this.telestrationGroup.selectAll(".telestration-stroke");
        let removed = false;
        paths.each(function () {
            if (removed) return;
            const pathEl = this as unknown as SVGGeometryElement;
            if (typeof (pathEl as any).isPointInStroke === "function" && (pathEl as any).isPointInStroke(domPt)) {
                // remove the path element
                pathEl.remove();
                removed = true;
            }
        });
    }

    private eraseAlongSegment(prev: SVGPoint | null, curr: SVGPoint) {
        if (!prev) {
            this.eraseAtPoint(curr);
            return;
        }

        const dx = curr.x - prev.x;
        const dy = curr.y - prev.y;
        const dist = Math.hypot(dx, dy);
        const step = 4;
        const steps = Math.max(1, Math.ceil(dist / step));
        for (let i = 0; i <= steps; i++) {
            const t = i / steps;
            const p: SVGPoint = { x: prev.x + dx * t, y: prev.y + dy * t } as any;
            this.eraseAtPoint(p);
        }
    }

    public svgPoint(dragEvent: any,
                    svgElement: SVGSVGElement,
                    mainGroupElement: SVGSVGElement): SVGPoint {
        const pt = svgElement.createSVGPoint();
        let sourceEvent: any;
        if (dragEvent.identifier === 'mouse') {
            sourceEvent = dragEvent.sourceEvent;
        } else if (dragEvent.sourceEvent.touches) {
            sourceEvent = dragEvent.sourceEvent.touches[dragEvent.identifier];
        }

        if (sourceEvent) {
            pt.x = sourceEvent.clientX;
            pt.y = sourceEvent.clientY;
        } else {
            pt.x = dragEvent.x;
            pt.y = dragEvent.y;
        }

        //console.debug("pt: ", pt.x, pt.y);
        
        const svgPt = pt.matrixTransform(mainGroupElement.getScreenCTM().inverse());
        //console.debug("svgPt: ", svgPt.x, svgPt.y);
        return svgPt;
    }
    
    public async startHeroAnimation() {
        // Prevent multiple animations from running
        if (this.heroAnimationRunning) {
            await this.stopHeroAnimation();
        }

        this.heroAnimationRunning = true;
        this.heroAnimationTimeouts = [];

        // loop through all regions, and slowly zoom in and back out of each
        const regions = this.regions;
        let currentRegionIndex = 1;

        const zoomInOut = () => {
            // Check if animation should continue
            if (!this.heroAnimationRunning) {
                return;
            }

            if (currentRegionIndex > regions.length) {
                currentRegionIndex = 0;
            }

            const region = currentRegionIndex == 0 ? null : this.regions[currentRegionIndex-1];
            let regionFeatures = (!region) ? this.geoJson.features : this.geoJson.features.filter(feature =>
                this.countries.get(region.regionCode).find(c => 
                    c.countryCode === feature.properties["ISO_A2"] && this.allEntities.get(c.countryCode))
            );
            if (regionFeatures.length === 0) {
                regionFeatures = this.geoJson.features.filter(feature =>
                    this.countries.get(region.regionCode).find(c =>
                        c.countryCode === feature.properties["ISO_A2"])
                );
            }

            // Zoom in to region
            this.selectedRegionCode = region?.regionCode;
            this.zoomToRegion(regionFeatures, false, 8000);

            // Zoom to the next region after a delay
            const nextAnimationTimeout = setTimeout(() => {
                if (this.heroAnimationRunning) {
                    currentRegionIndex++;
                    zoomInOut();
                }
            }, 8000);
            this.heroAnimationTimeouts.push(nextAnimationTimeout);
        };

        zoomInOut();

    }
    
    public async stopHeroAnimation() {
        this.heroAnimationRunning = false;
        this.selectedRegionCode = null;

        // Clear all pending timeouts
        this.heroAnimationTimeouts.forEach(timeout => {
            clearTimeout(timeout);
        });
        this.heroAnimationTimeouts = [];
        
        // Reset to Global view
        await this.transitionToGlobal(1000);
    }

    public async visualizeFutureStep(stepSummary: IFutureStateStepSummary) {
        // console.debug('visualizeFutureStep', stepSummary);
        this.selectedStepSummary = stepSummary;
        this.filteredFlowLinks = [];
        this.visualizationCaptions = [];
        await this.dotNetObjectReference.invokeMethodAsync("OnRegionSelectedAsync", null);

        if (!stepSummary) {
            await this.clearVisualization();
            await this.transitionToGlobal();
        } else {
            this.clearFlows();
            await this.hidePopups();

            if (!this.isVisualizing && this.mapMode === MapMode.Flows) {
                // if this is the first click on a step while in Flow mode, draw icons
                this.generateIcons();
                this.drawIcons();
            }

            this.isVisualizing = true;
            this.isTransitioning = true;

            // disable all pointer events and panning/zooming
            this.disableAllPointerEvents(true);

            // unhide all regions because this step could involve any country(s) on the map
            this.hideRegionsExcept(null);

            if (isAddEntityStepSummary(stepSummary)) {
                await this.visualizeAddEntityStep(stepSummary);
            } else if (isAddFunctionStepSummary(stepSummary)) {
                await this.visualizeAddFunctionStep(stepSummary);
            } else if (isMoveFunctionStepSummary(stepSummary)) {
                await this.visualizeMoveFunctionStep(stepSummary);
            } else if (isAdjustFeeStepSummary(stepSummary)) {
                await this.visualizeTransactionStep(stepSummary);
            } else if (isIPTransferStepSummary(stepSummary)) {
                await this.visualizeTransactionStep(stepSummary);
            } else if (isBuySellPrincipalStep(stepSummary)) {
                await this.visualizeBuySellPrincipalStep(stepSummary);
            } else if (isBuySellSuppliersStep(stepSummary)) {
                await this.visualizeBuySellSuppliersStep(stepSummary);
            } else if (isBuySellDistributorsStep(stepSummary)) {
                await this.visualizeBuySellDistributorsStep(stepSummary);
            } else if (isBuySellSummary(stepSummary)) {
                await this.visualizeBuySellSummaryStep(stepSummary);
            } else if (isMoveRevenueExpenseStepSummary(stepSummary)) {
                await this.visualizeMoveRevenueExpenseSummaryStep(stepSummary);
            } else if (isMoveRevenueExpenseDetailStepSummary(stepSummary)) {
                await this.visualizeMoveRevenueExpenseDetailStep(stepSummary);
            } else if (isMoveExpenseOrRevenueStep(stepSummary)) {
                await this.visualizeMoveExpenseOrRevenueStep(stepSummary);
            } else if (isLoanBorrowerStepSummary(stepSummary)) {
                await this.visualizeLoanBorrowerStep(stepSummary);
            } else if (isAddTaxRateStepSummary(stepSummary)
                || isEditTaxRateStepSummary(stepSummary)
                || isEditNolUtilizationRateStepSummary(stepSummary)
                || isEditInterestLimitationStepSummary(stepSummary)
                || isIpOwnerSummary(stepSummary)
                || isLoanLenderSummary(stepSummary)
                || isServiceProviderSummary(stepSummary)
                || isUpdateEntityTypesStepSummary(stepSummary)
                || isUpdateEntityTagsStepSummary(stepSummary)
                || isUpdateLineItemsStepSummary(stepSummary)) {
                await this.visualizeSingleEntityStep(stepSummary);
            } else if (isEntityRelationshipStepSummary(stepSummary)) {
                await this.visualizeRelationshipStepSummary(stepSummary);
            } else if (isCustomsStepSummary(stepSummary)) {
                await this.visualizeCustomsStep(stepSummary);
            }

            this.isTransitioning = false;
        }
    }

    public async clearVisualization() {
        this.selectedStepSummary = null;
        this.isVisualizing = false;
        // clear all visualizations
        this.clearFlows();
        this.filteredFlowLinks = [];
        this.visualizationCaptions = [];
        // un-dim all countries
        this.setCountryColorsOnHover(null);
        this.visualizationEntityIds = null;
        // clear drop-shadow from all countries
        this.svg.selectAll('path').classed("drop-shadow", false);
        // close any popups
        await this.hidePopups();
        // re-enable mouse events
        this.disableAllPointerEvents(false);
        this.mapContainer.parentElement.parentElement.style.pointerEvents = 'unset';
    }

    public async visualizeAddEntityStep(stepSummary: IAddEntityStepSummary) {
        const entity = this.allEntities.get(stepSummary.countryCode)?.find(e => e.entityId === stepSummary.entityId);
        if (!entity) {
            return;
        }

        // dim other countries
        this.setCountryColorsOnHover(stepSummary.countryCode);
        // dim all icons except the one being added
        this.visualizationEntityIds = [entity.entityId];
        this.countriesForVisualization = [stepSummary.countryCode];
        // zoom to country
        this.zoomToRegion([this.geoJson.features.find(feature => feature.properties["ISO_A2"] === stepSummary.countryCode)], true);
        // add drop shadow to this country and remove it from all others:
        this.svg.selectAll('path').classed("drop-shadow", false);
        this.svg.select(`#${stepSummary.countryCode}`).classed("drop-shadow", true);

        this.clearFlows();
    }

    public async visualizeSingleEntityStep(stepSummary: ISingleEntityStepSummary) {
        //console.debug('visualizeSingleEntityStep', stepSummary);
        const entity = this.allEntities.get(stepSummary.countryCode)?.find(e => e.entityId === stepSummary.entityId);
        if (!entity) {
            return;
        }

        // dim other countries
        this.setCountryColorsOnHover(stepSummary.countryCode);
        // dim all icons except the one being added
        this.visualizationEntityIds = [entity.entityId];
        this.countriesForVisualization = [stepSummary.countryCode];
        // zoom to country
        this.zoomToRegion([this.geoJson.features.find(feature => feature.properties["ISO_A2"] === stepSummary.countryCode)], true);
        // add drop shadow to this country and remove it from all others:
        this.svg.selectAll('path').classed("drop-shadow", false);
        this.svg.select(`#${stepSummary.countryCode}`).classed("drop-shadow", true);

        this.clearFlows();
    }

    public async visualizeAddFunctionStep(stepSummary: IAddFunctionStepSummary) {
        var entity = this.allEntities.get(stepSummary.countryCode)?.find(e => e.entityId === stepSummary.entityId);
        if (!entity) {
            return;
        }

        // dim other countries
        this.setCountryColorsOnHover(stepSummary.countryCode);
        // dim all icons except the one being added
        this.visualizationEntityIds = [entity.entityId];
        this.countriesForVisualization = [stepSummary.countryCode];
        // zoom to country
        this.zoomToRegion([this.geoJson.features.find(feature => feature.properties["ISO_A2"] === stepSummary.countryCode)], true);
        // add drop shadow to this country and remove it from all others:
        this.svg.selectAll('path').classed("drop-shadow", false);
        this.svg.select(`#${stepSummary.countryCode}`).classed("drop-shadow", true);

        this.clearFlows();
    }

    public async visualizeRelationshipStepSummary(stepSummary: IEntityRelationshipStepSummary) {
        const sourceEntities = Array.from(this.allEntities.values()).flat().filter(e => stepSummary.sourceEntityIds.includes(e.entityId));
        const targetEntities = Array.from(this.allEntities.values()).flat().filter(e => stepSummary.targetEntityIds.includes(e.entityId));
        const allEntities = sourceEntities.concat(targetEntities);

        // dim other countries
        this.setCountryColorsOnHover(stepSummary.sourceCountryCodes.concat(stepSummary.targetCountryCodes));
        // dim all icons except the ones involved in the relationship
        this.visualizationEntityIds = allEntities.map(e => e.entityId);
        this.countriesForVisualization = stepSummary.sourceCountryCodes.concat(stepSummary.targetCountryCodes);
        // zoom to source and target
        this.zoomToRegion(this.countriesForVisualization.map(countryCode => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)),
            true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        stepSummary.sourceCountryCodes.concat(stepSummary.targetCountryCodes).forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();

        if (isAddRelationshipStepSummary(stepSummary)) {
            // get the link type to visualize
            let linkType = flowTypeToLinkClassName(stepSummary.flowType);

            // draw links between source and target entities
            const newLinks: ILink[] = [];
            sourceEntities.forEach(sourceEntity => {
                targetEntities.forEach(targetEntity => {
                    const sourceElement = this.flowElements.find(e => e.entityIds.includes(sourceEntity.entityId));
                    const targetElement = this.flowElements.find(e => e.entityIds.includes(targetEntity.entityId));

                    if (!sourceElement || !targetElement) {
                        return;
                    }

                    let linkStyle = flowTypeStyles.find(f => f.type === linkType);

                    if (!linkStyle) {
                        return;
                    }

                    newLinks.push({
                        type: linkType,
                        source: {id: sourceElement.id},
                        target: {id: targetElement.id},
                        color: linkStyle.color,
                        dashed: linkStyle.dashed,
                    } as ILink);
                });
            });

            this.filteredFlowLinks = newLinks;

            setTimeout(() => {
                this.drawLinksBetweenCapsules();
            }, 1400);
        }
    }

    public async visualizeMoveFunctionStep(stepSummary: IMoveFunctionStepSummary) {
        var entitiesForStep = [];
        stepSummary.functionMoveRelationships.forEach(relationship => {
            entitiesForStep = entitiesForStep.concat(
                this.allEntities.get(relationship.sourceCountryCode).filter(e => e.entityId === relationship.sourceEntityId)).concat(
                    this.allEntities.get(relationship.targetCountryCode).filter(e => e.entityId === relationship.targetEntityId));
        });

        var countriesForStep = Array.from(new Set(stepSummary.functionMoveRelationships.map(r => r.sourceCountryCode).concat(stepSummary.functionMoveRelationships.map(r => r.targetCountryCode))));

        // dim other countries
        this.setCountryColorsOnHover(countriesForStep);
        // dim all icons except the ones involved in the function move
        this.visualizationEntityIds = entitiesForStep.map(e => e.entityId);
        this.countriesForVisualization = countriesForStep;
        // zoom to source and target countries
        this.zoomToRegion(countriesForStep.map(countryCode => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)), true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        countriesForStep.forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();

        var newLinks: ILink[] = stepSummary.functionMoveRelationships.map(fmr => {
            var sourceElement = this.flowElements.find(e => e.entityIds.includes(fmr.sourceEntityId));
            var targetElement = this.flowElements.find(e => e.entityIds.includes(fmr.targetEntityId));
            return {
                type: "ServiceLink",
                source: { id: sourceElement.id },
                target: { id: targetElement.id },
                color: "black",
                dashed: false
            } as ILink;
        });

        this.filteredFlowLinks = newLinks;

        setTimeout(() => {
            this.drawLinksBetweenCapsules();
        }, 1400);
    }

    private getEntitiesByEntityIds(entityIds: string[]): IEntityDto[] {
        const entityIdSet = new Set(entityIds);
        return Array.from(this.allEntities.values()).flat().filter(e => entityIdSet.has(e.entityId));
    }

    private getCountryCodesForEntities(entities: IEntityDto[]): string[] {
        return Array.from(new Set(entities.map(e => e.countryCode)));
    }

    public async visualizeMoveRevenueExpenseSummaryStep(stepSummary: IMoveRevenueExpenseStepSummary) {
        const entityIdsForStep = Array.from(new Set(stepSummary.sourceEntityIds.concat(stepSummary.targetEntityIds)));
        const entitiesForStep = this.getEntitiesByEntityIds(entityIdsForStep);

        if (!entitiesForStep.length) {
            return;
        }

        const countryCodesForStep = this.getCountryCodesForEntities(entitiesForStep);

        this.setCountryColorsOnHover(countryCodesForStep);
        this.visualizationEntityIds = entityIdsForStep;
        this.countriesForVisualization = countryCodesForStep;

        this.zoomToRegion(
            countryCodesForStep.map(countryCode => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)),
            true
        );

        this.svg.selectAll('path').classed("drop-shadow", false);
        countryCodesForStep.forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();

        if (stepSummary.sourceEntityIds.length === 1 && stepSummary.targetEntityIds.length === 1) {
            const sourceEntityId = stepSummary.sourceEntityIds[0];
            const targetEntityId = stepSummary.targetEntityIds[0];
            const sourceElement = this.flowElements.find(e => e.entityIds.includes(sourceEntityId));
            const targetElement = this.flowElements.find(e => e.entityIds.includes(targetEntityId));

            if (!sourceElement || !targetElement) {
                return;
            }

            this.filteredFlowLinks = [
                {
                    type: "ServiceLink",
                    source: { id: sourceElement.id },
                    target: { id: targetElement.id },
                    color: "black",
                    dashed: false
                } as ILink
            ];

            setTimeout(() => {
                this.drawLinksBetweenCapsules();
            }, 1400);
        }
    }

    public async visualizeMoveRevenueExpenseDetailStep(stepSummary: IMoveRevenueExpenseDetailStepSummary) {
        const sourceEntityId = stepSummary.sourceEntityId;
        const targetEntityId = stepSummary.targetEntityId;

        if (!sourceEntityId || !targetEntityId) {
            return;
        }

        const entitiesForStep = this.getEntitiesByEntityIds([sourceEntityId, targetEntityId]);
        const countryCodesForStep = this.getCountryCodesForEntities(entitiesForStep);

        if (!countryCodesForStep.length) {
            return;
        }

        this.setCountryColorsOnHover(countryCodesForStep);
        this.visualizationEntityIds = [sourceEntityId, targetEntityId];
        this.countriesForVisualization = countryCodesForStep;

        this.zoomToRegion(
            countryCodesForStep.map(countryCode => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)),
            true
        );

        this.svg.selectAll('path').classed("drop-shadow", false);
        countryCodesForStep.forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();

        const sourceElement = this.flowElements.find(e => e.entityIds.includes(sourceEntityId));
        const targetElement = this.flowElements.find(e => e.entityIds.includes(targetEntityId));

        if (!sourceElement || !targetElement) {
            return;
        }

        this.filteredFlowLinks = [
            {
                type: "ServiceLink",
                source: { id: sourceElement.id },
                target: { id: targetElement.id },
                color: "black",
                dashed: false
            } as ILink
        ];

        setTimeout(() => {
            this.drawLinksBetweenCapsules();
        }, 1400);
    }

    public async visualizeCustomsStep(stepSummary: ICustomsStepSummary) {
        // for Customs steps, source entity may be external/third-party, so we may only have a target entity and target country
        var entitiesForStep = this.allEntities.get(stepSummary.targetCountryCode).filter(e => e.entityId === stepSummary.targetEntityId);
        if (stepSummary.sourceCountryCode){
            entitiesForStep = entitiesForStep.concat(
                this.allEntities.get(stepSummary.sourceCountryCode).filter(e => e.entityId === stepSummary.sourceEntityId));
        }

        // dim all icons except the ones involved in the fee adjustment
        this.visualizationEntityIds = entitiesForStep.map(e => e.entityId);
        this.countriesForVisualization = stepSummary.sourceCountryCode 
            ? [stepSummary.sourceCountryCode, stepSummary.targetCountryCode]
            : [stepSummary.targetCountryCode];
        
        // dim other countries
        this.setCountryColorsOnHover(this.countriesForVisualization);

        // zoom to source and target countries
        this.zoomToRegion(this.geoJson.features.filter(feature => this.countriesForVisualization.includes(feature.properties["ISO_A2"])),
            true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        this.svg.select(`#${stepSummary.sourceCountryCode}`).classed("drop-shadow", true);
        this.svg.select(`#${stepSummary.targetCountryCode}`).classed("drop-shadow", true);

        const sourceElement = this.flowElements.find(e => e.entityIds.includes(stepSummary.sourceEntityId));
        const targetElement = this.flowElements.find(e => e.entityIds.includes(stepSummary.targetEntityId));
        if (!sourceElement || !targetElement) {
            console.warn("Source or target element not found for customs step:", stepSummary);
            return;
        }
            
        this.filteredFlowLinks = [
            {
                type: "PhysicalLink",
                source: {id: sourceElement.id},
                target: {id: targetElement.id},
                color: "#CE1210",
                dashed: false
            } as ILink
        ];

        this.clearFlows();

        setTimeout(() => {
            this.drawLinksBetweenCapsules();
        }, 1400);
    }

    public async visualizeTransactionStep(stepSummary: IAdjustFeeStepSummary | IIPTransferStepSummary) {
        var entitiesForStep =
            this.allEntities.get(stepSummary.sourceCountryCode).filter(e => e.entityId === stepSummary.sourceEntityId).concat(
                this.allEntities.get(stepSummary.targetCountryCode).filter(e => e.entityId === stepSummary.targetEntityId));

        var entityIdsForStep = [stepSummary.sourceEntityId, stepSummary.targetEntityId];

        // dim other countries
        this.setCountryColorsOnHover([stepSummary.sourceCountryCode, stepSummary.targetCountryCode]);

        // dim all icons except the ones involved in the fee adjustment
        this.visualizationEntityIds = entitiesForStep.map(e => e.entityId);
        this.countriesForVisualization = [stepSummary.sourceCountryCode, stepSummary.targetCountryCode];

        // zoom to source and target countries
        this.zoomToRegion([this.geoJson.features.find(feature => feature.properties["ISO_A2"] === stepSummary.sourceCountryCode),
        this.geoJson.features.find(feature => feature.properties["ISO_A2"] === stepSummary.targetCountryCode)],
            true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        this.svg.select(`#${stepSummary.sourceCountryCode}`).classed("drop-shadow", true);
        this.svg.select(`#${stepSummary.targetCountryCode}`).classed("drop-shadow", true);

        let flowTypesToInclude = ["PaymentLink"];
        // check the step summary title to know if this is a Service or Royalty fee adjustment
        if (stepSummary.title.includes("Service") || stepSummary.$type.includes("Service")) {
            flowTypesToInclude.push("ServiceLink");
        } else if (stepSummary.title.includes("IP Transfer") || stepSummary.$type.includes("IPTransfer")) {
            flowTypesToInclude.push("IPTransferLink");
        } else {
            flowTypesToInclude.push("IPLicenseLink");
        }

        const transactionFlowLinks = new Set<ILink>();
        this.flowLinks.forEach(link => {
            const sourceElement = this.flowElements.find(e => e.id === link.source.id);
            const targetElement = this.flowElements.find(e => e.id === link.target.id);

            if (this.shouldDrawLinkForStep(link, entityIdsForStep, flowTypesToInclude, stepSummary.sourceEntityId, stepSummary.targetEntityId)
                && ((link.type === "PaymentLink" && sourceElement.entityIds.includes(stepSummary.sourceEntityId))
                || (link.type !== "PaymentLink" && targetElement.entityIds.includes(stepSummary.sourceEntityId)))) {
                transactionFlowLinks.add(link);
            }
        });

        this.filteredFlowLinks = Array.from(transactionFlowLinks);

        this.clearFlows();

        setTimeout(() => {
            this.drawLinksBetweenCapsules();
        }, 1400)
    }

    public async visualizeBuySellPrincipalStep(stepSummary: IBuySellPrincipalStep) {
        var countryCodesForStep = [stepSummary.pocTradeCoCountryCode];
        var entityIdsForStep = [stepSummary.pocTradeCoEntityId];

        var entitiesForStep = Array.from(this.allEntities.values()).flat().filter(e => entityIdsForStep.includes(e.entityId));

        // dim other countries
        this.setCountryColorsOnHover(countryCodesForStep);
        // dim all icons except the ones involved in the buy/sell
        this.visualizationEntityIds = entityIdsForStep;
        this.countriesForVisualization = countryCodesForStep;

        // zoom to relevant countries
        this.zoomToRegion(countryCodesForStep.map((countryCode: any) => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)), true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        countryCodesForStep.forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();
    }

    public async visualizeBuySellSuppliersStep(stepSummary: IBuySellSuppliersStep) {
        var countryCodesForStep = stepSummary.supplierCountryCodes.concat([stepSummary.pocTradeCoCountryCode]);
        var entityIdsForStep = stepSummary.supplierEntityIds.concat([stepSummary.pocTradeCoEntityId]);

        // dim other countries
        this.setCountryColorsOnHover(countryCodesForStep);
        // dim all icons except the ones involved in the buy/sell
        this.visualizationEntityIds = entityIdsForStep;
        this.countriesForVisualization = countryCodesForStep;

        // zoom to relevant countries
        this.zoomToRegion(countryCodesForStep.map(countryCode => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)), true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        countryCodesForStep.forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();

        let flowTypesToInclude = ["PaymentLink", "LegalLink"];

        this.filteredFlowLinks = this.flowLinks.filter(link => {
            return this.shouldDrawLinkForStep(link, entityIdsForStep, flowTypesToInclude);
        });


        // draw routine return labels for each manufacturer
        Object.entries(stepSummary.supplierReturnLabels).forEach(([supplierEntityId, returnLabel]) => {
            this.visualizationCaptions.push([supplierEntityId, returnLabel])
        });

        setTimeout(() => {
            this.drawLinksBetweenCapsules();
        }, 1400);
    }

    public async visualizeBuySellDistributorsStep(stepSummary: IBuySellDistributorsStep) {
        var countryCodesForStep = stepSummary.distributorCountryCodes.concat([stepSummary.pocTradeCoCountryCode]);
        var entityIdsForStep = stepSummary.distributorEntityIds.concat([stepSummary.pocTradeCoEntityId]);

        // dim other countries
        this.setCountryColorsOnHover(countryCodesForStep);
        // dim all icons except the ones involved in the buy/sell
        this.visualizationEntityIds = entityIdsForStep;
        this.countriesForVisualization = countryCodesForStep;

        // zoom to relevant countries
        this.zoomToRegion(countryCodesForStep.map(countryCode => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)), true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        countryCodesForStep.forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();

        let flowTypesToInclude = ["PaymentLink", "LegalLink"];

        this.filteredFlowLinks = this.flowLinks.filter(link => {
            return this.shouldDrawLinkForStep(link, entityIdsForStep, flowTypesToInclude);
        });

        // draw routine return labels for each Distributor
        Object.entries(stepSummary.distributorReturnLabels).forEach(([distributorEntityId, returnLabel]) => {
            this.visualizationCaptions.push([distributorEntityId, returnLabel])
        });

        setTimeout(() => {
            this.drawLinksBetweenCapsules();
        }, 1400);
    }

    public async visualizeBuySellSummaryStep(stepSummary: IBuySellSummaryStep) {
        var countryCodesForStep = stepSummary.distributorCountryCodes.concat(stepSummary.supplierCountryCodes).concat([stepSummary.pocTradeCoCountryCode]);
        var entityIdsForStep = stepSummary.distributorEntityIds.concat(stepSummary.supplierEntityIds).concat([stepSummary.pocTradeCoEntityId]);

        // dim other countries
        this.setCountryColorsOnHover(countryCodesForStep);
        // dim all icons except the ones involved in the buy/sell
        this.visualizationEntityIds = entityIdsForStep;
        this.countriesForVisualization = countryCodesForStep;

        // zoom to relevant countries
        this.zoomToRegion(countryCodesForStep.map(countryCode => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)), true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        countryCodesForStep.forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();

        let flowTypesToInclude = ["PaymentLink", "LegalLink", "PhysicalLink"];

        this.filteredFlowLinks = this.flowLinks.filter(link => {
            return this.shouldDrawLinkForStep(link, entityIdsForStep, flowTypesToInclude);
        });

        setTimeout(() => {
            this.drawLinksBetweenCapsules();
        }, 1400);
    }

    public async visualizeMoveExpenseOrRevenueStep(stepSummary: IMoveExpenseOrRevenueStep) {
        var countryCodesForStep = [stepSummary.sourceCountryCode, stepSummary.targetCountryCode];

        // dim other countries
        this.setCountryColorsOnHover(countryCodesForStep);
        // dim all icons except the ones involved in the function move
        this.visualizationEntityIds = [stepSummary.sourceEntityId, stepSummary.targetEntityId];
        this.countriesForVisualization = countryCodesForStep;
        // zoom to source and target countries
        this.zoomToRegion(countryCodesForStep.map(countryCode => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)), true);

        // add drop shadow to these countries and remove it from all others
        this.svg.selectAll('path').classed("drop-shadow", false);
        countryCodesForStep.forEach(countryCode => {
            this.svg.select(`#${countryCode}`).classed("drop-shadow", true);
        });

        this.clearFlows();

        // create the arrow for visualizing the move
            var sourceElement = this.flowElements.find(e => e.entityIds.includes(stepSummary.sourceEntityId));
            var targetElement = this.flowElements.find(e => e.entityIds.includes(stepSummary.targetEntityId));
            var newLink = {
                type: "ServiceLink",
                source: { id: sourceElement.id },
                target: { id: targetElement.id },
                color: "black",
                dashed: false
            } as ILink;

        this.filteredFlowLinks = [newLink];

        setTimeout(() => {
            this.drawLinksBetweenCapsules();
        }, 1400);
    }

    // Visualize Loan sub summaries to show borrower -> lender payment arrow
    public async visualizeLoanBorrowerStep(stepSummary: ILoanBorrowerStepSummary) {
        const allEntitiesFlat = Array.from(this.allEntities.values()).flat();
        const lenderId = stepSummary.lenderEntityId;
        const borrowerId = stepSummary.borrowerEntityId;

        const lender = allEntitiesFlat.find(e => e.entityId === lenderId);
        const borrower = allEntitiesFlat.find(e => e.entityId === borrowerId);

        if (!lender || !borrower) {
            return;
        }

        const countryCodes = Array.from(new Set([lender.countryCode, borrower.countryCode]));

        // dim other countries
        this.setCountryColorsOnHover(countryCodes);
        // dim all icons except lender and borrowers
        this.visualizationEntityIds = [lender.entityId, borrower.entityId];
        this.countriesForVisualization = countryCodes;

        // zoom to involved countries
        this.zoomToRegion(countryCodes.map(cc => this.geoJson.features.find(feature => feature.properties["ISO_A2"] === cc)), true);

        // add drop shadow
        this.svg.selectAll('path').classed("drop-shadow", false);
        countryCodes.forEach(cc => this.svg.select(`#${cc}`).classed("drop-shadow", true));

        // Build arrows borrower -> lender
        const links: ILink[] = [];
        if (borrower) {
            const sourceElement = this.flowElements.find(e => e.entityIds.includes(borrower.entityId));
            const targetElement = this.flowElements.find(e => e.entityIds.includes(lender.entityId));
            if (sourceElement && targetElement) {
                const style = flowTypeStyles.find(f => f.type === 'PaymentLink');
                links.push({
                    type: 'PaymentLink',
                    source: { id: sourceElement.id },
                    target: { id: targetElement.id },
                    color: style?.color || '#9B9B9D',
                    dashed: style?.dashed || true,
                    id: `${sourceElement.id}-${targetElement.id}-loan`
                } as any as ILink);
            }
        }

        this.filteredFlowLinks = links;
        this.clearFlows();
        setTimeout(() => {
            this.drawLinksBetweenCapsules(this.filteredFlowLinks);
        }, 1400);
    }

    private shouldDrawLinkForStep(link: ILink, entityIdsForStep: string[], linkTypesForStep: string[], sourceEntityId?: string, targetEntityId?: string): boolean {
        // Return false if the link type is not in the allowed types
        if (linkTypesForStep && !linkTypesForStep.includes(link?.type)) {
            return false;
        }

        // Flow links
        const sourceElement = this.flowElements.find(e => e.id === link.source.id);
        const targetElement = this.flowElements.find(e => e.id === link.target.id);

        // Ensure both source and target elements exist
        if (!sourceElement || !targetElement) {
            return false;
        }

        return (
            sourceElement.entityIds.some(id => entityIdsForStep.includes(id)) ||
            targetElement.entityIds.some(id => entityIdsForStep.includes(id))
        );
    }

    public checkAreaForCollisions(x: number, y: number, width: number, height: number): boolean {
        let collides = false;

        // check other SVG elements (capsules, icons, and captions) for collisions
        const capsuleElements = this.svg.selectAll('g[id^="capsule"]');
        const iconElements = this.svg.selectAll('g[id^="icon"]');

        const svgElements: any[] = Array.from(capsuleElements).concat(Array.from(iconElements));
        for (let i = 0; i < svgElements.length; i++) {
            const element = svgElements[i];
            const bbox = element.getBBox();
            const ctm = element.getScreenCTM();
            const elementX = bbox.x * ctm.a + bbox.y * ctm.c + ctm.e;
            const elementY = bbox.x * ctm.b + bbox.y * ctm.d + ctm.f;
            const elementWidth = bbox.width;
            const elementHeight = bbox.height;

            collides = !(x + width < elementX || x > elementX + elementWidth || y + height < elementY || y > elementY + elementHeight);

            if (collides) {
                return true;
            }
        }

        // check DOM elements (arrows, future-state-steps-container and metric-body) for collisions
        const domElements = document.querySelectorAll('.arrow__path, .future-state-steps-container, .metric-body');
        domElements.forEach(domElement => {
            // can't break out of a forEach loop by returning, so if `collides` is already set to true, just skip the rest of the logic.
            if (collides)
                return;

            // if the element is not visible, skip it (no issues colliding with invisible elements)
            if (!domElement.checkVisibility({ opacityProperty: true, visibilityProperty: true }))
                return;

            const bbox = domElement.getBoundingClientRect();
            const arrowX = bbox.x;
            const arrowY = bbox.y;
            const arrowWidth = bbox.width;
            const arrowHeight = bbox.height;

            collides = collides || !(x + width < arrowX || x > arrowX + arrowWidth || y + height < arrowY || y > arrowY + arrowHeight);
        });

        return collides;
    }

    public setFlows(elements: IElement[], links: ILink[]) {
        if (!arraysAreEqual(elements, this.flowElements) || !arraysAreEqual(links, this.flowLinks)) {
            this.flowElements = elements;

            const elementIds = new Set(this.flowElements.map(e => e.id));

            // Resolve links that point to intermediate (non-element) nodes by following
            // chains of same-type/color links until a real element target is found.
            const linkLookup = links.filter(l => l?.source?.id && l?.target?.id);
            const resolvedLinks: ILink[] = [];
            const dedupKeys = new Set<string>();

            const addResolvedLink = (link: ILink) => {
                const key = `${link.type}|${link.color}|${link.dashed}|${link.source.id}|${link.target.id}`;
                if (!dedupKeys.has(key)) {
                    dedupKeys.add(key);
                    resolvedLinks.push(link);
                }
            };

            const resolveLink = (link: ILink, visitedTargets: Set<string>): void => {
                // Drop links whose source is not a known element
                if (!elementIds.has(link.source.id)) {
                    return;
                }

                // If target exists, we have a concrete link
                if (elementIds.has(link.target.id)) {
                    addResolvedLink(link);
                    return;
                }

                // Prevent infinite loops when following intermediate hops
                if (visitedTargets.has(link.target.id)) {
                    return;
                }

                const updatedVisited = new Set(visitedTargets);
                updatedVisited.add(link.target.id);

                const nextLinks = linkLookup.filter(l =>
                    l.source.id === link.target.id && l.type === link.type && l.color === link.color);

                if (nextLinks.length === 0) {
                    return;
                }

                nextLinks.forEach(nextLink => {
                    const chainedLink: ILink = {
                        ...link,
                        target: nextLink.target,
                        id: `${link.id || link.source.id}-${nextLink.id || nextLink.target.id}`
                    };

                    resolveLink(chainedLink, updatedVisited);
                });
            };

            links.forEach(link => resolveLink(link, new Set()));

            this.flowLinks = resolvedLinks;
        }
    }

    public clearFlows() {
        d3.selectAll('.arrow').remove();
    }

    public drawFlows(animate: boolean = false) {
        //console.debug("Drawing flows", animate);
        this.clearFlows();
        // Get all unique entityIds from the flowElements
        const allFlowEntityIds = this.flowElements.map(e => e.entityIds).flat();
        const uniqueFlowEntityIds = [...new Set(allFlowEntityIds)];

        // Filter for entities that should be visualized in the flow
        const filteredEntityIds = this.isVisualizing && this.visualizationEntityIds
            ? this.visualizationEntityIds
            : Array.from(this.filteredEntities.values()).flat().map(e => e.entityId);
        const filteredFlowEntityIds = uniqueFlowEntityIds.filter(id => filteredEntityIds.includes(id));

        // Get flowElements where any of the entityIds are in the entitiesForFlow
        const elementsForFlow = this.flowElements.filter(e => e.entityIds.some(id => filteredFlowEntityIds.includes(id))).map(e => ({ ...e }));

        // Draw nodes for flow elements
        elementsForFlow.forEach(element => {
            // filter out entityIds that are not in the filteredEntityIds
            element.entityIds = element.entityIds.filter(id => filteredEntityIds.includes(id));
        });

        // Draw links between capsules excluding 'all changes' step when in Entity View
        if (!(this.mapMode === MapMode.Entities && this.selectedStepSummary == null)) {
            window.setTimeout(() => {
                this.drawLinksBetweenCapsules();
            }, animate ? 1100 : 100);
        }

    }

    // called from VisualizeBuySellSummaryStep
    public drawLinksBetweenCapsules(links: ILink[] | null = null) {
        d3.selectAll('.arrow').remove();

        // uniqueArrows is a Map where key = arrow JSON (to avoid duplicate arrows) and value = all the link ids represented by that arrow
        const uniqueArrows = new Map<string, string[]>();
        const sourceTargetFlowCount = new Map<string, number>();

        if (!links) {
            if (this.filteredFlowLinks?.length > 0) {
                // if there are filtered flow links, use them
                links = this.filteredFlowLinks;
            } else {
                links = this.flowLinks ?? [];
            }
        }

        links.forEach(link => {
            // Do not draw Ownership links, or links with types/colors that are currently set to be hidden
            if (link.type === 'OwnershipLink' || this.flowTypesToHide.includes(link.type) || this.flowTypesToHide.includes(link.color)) {
                return;
            }
            
            const sourceElement = this.flowElements.find(e => e.id === link.source.id);
            const targetElement = this.flowElements.find(e => e.id === link.target.id);
            if (!sourceElement || !targetElement ) {
                return;
            }
            
            const filteredEntityIds = this.isVisualizing && this.visualizationEntityIds
                ? this.visualizationEntityIds
                : Array.from(this.filteredEntities.values()).flat().map(e => e.entityId);

            // In case there are multiple entities in the source and/or target elements, group by region on both ends
            const allEntities = Array.from(this.allEntities.values()).flat();
            const sourceEntities = allEntities.filter(e => 
                sourceElement.entityIds.includes(e.entityId) && filteredEntityIds.includes(e.entityId));
            const targetEntities = allEntities.filter(e => 
                targetElement.entityIds.includes(e.entityId) && filteredEntityIds.includes(e.entityId));

            // Add arrows between each source/target combination
            sourceEntities.forEach((sourceEntity, sourceRegion) => {
                targetEntities.forEach((targetEntity, targetRegion) => {
                    let sourceId: string, targetId: string;
                    // at country level (or when visualizing a step) sourceId and targetId will be the countryCode
                    if (this.selectedCountryCode || this.isVisualizing) {
                        sourceId = sourceEntity.countryCode;
                        targetId = targetEntity.countryCode;
                    }
                    // at regional level, sourceId and targetId will be the countryCode
                    else if (this.selectedRegionCode) {
                        sourceId = sourceEntity.countryCode;
                        targetId = targetEntity.countryCode;
                    }
                    // at global level, sourceId and targetId will be the regionCode
                    else {
                        sourceId = sourceEntity.regionCode;
                        targetId = targetEntity.regionCode;
                    }

                    const arrow: IFlowArrow = { type: link.type, sourceId: sourceId, targetId: targetId, color: link.color, dashed: link.dashed };
                    var arrowJson = JSON.stringify(arrow);
                    // add to uniqueArrows to avoid duplicate arrows and track the link id
                    const linkIds = uniqueArrows.get(arrowJson) || [];
                    linkIds.push(link.id);
                    uniqueArrows.set(arrowJson, linkIds);
                });
            });
        });

        Array.from(uniqueArrows.keys()).forEach(arrowJSON => {
            const arrow = JSON.parse(arrowJSON) as IFlowArrow;

            // increment the count of flows between these this source-target pairing for proper offsetting
            const offset = sourceTargetFlowCount.get(`${arrow.sourceId}-${arrow.targetId}`) || 1;
            sourceTargetFlowCount.set(`${arrow.sourceId}-${arrow.targetId}`, offset + 1);
        });

        Array.from(uniqueArrows.keys()).forEach(arrowJSON => {
            const arrow = JSON.parse(arrowJSON) as IFlowArrow;

            // now decrement the count of flows between these this source-target pairing as we draw each one
            const offset = sourceTargetFlowCount.get(`${arrow.sourceId}-${arrow.targetId}`) || 1;
            sourceTargetFlowCount.set(`${arrow.sourceId}-${arrow.targetId}`, offset - 1);

            const linkIds = uniqueArrows.get(arrowJSON);
            this.drawFlowArrowBetweenCapsules(arrow, offset - 1, linkIds);
        });
    }

    private drawFlowArrowBetweenCapsules(arrow: IFlowArrow, offset: number = 1, linkIds: string[]) {
        const sourceCapsule = document.getElementById(`capsule${arrow.sourceId}`);
        const targetCapsule = document.getElementById(`capsule${arrow.targetId}`);
        if (!sourceCapsule || !targetCapsule) {
            return;
        }

        // TODO: Refactor (this is copied from drawFlowArrowBetweenIcons function)
        // calculate the dx and dy between source and target elements:
        const sourceRect = sourceCapsule.getBoundingClientRect();
        const targetRect = targetCapsule.getBoundingClientRect();
        const dx = targetRect.left - sourceRect.left;
        const dy = targetRect.top - sourceRect.top;

        // Determine if the target is to the top, bottom, left, or right of the source
        const isTargetTop = dy < 0;
        const isTargetLeft = dx < 0;

        const translateXwise = Math.abs(dy) > Math.abs(dx);

        // Euclidean distance between source and target
        const distance = Math.sqrt(dx * dx + dy * dy);

        // px distance between source and target at which the curve starts to reduce
        const minDistance = 50;
        const maxDistance = 1000;
        const normalizedDistance = Math.min(Math.max(distance, minDistance), maxDistance);

        const minCurveReduction = 0.3;
        const maxCurveReduction = 1.5;

        let curveReductionFactor = Math.min(
            maxCurveReduction,
            Math.max(minCurveReduction, 1 - (normalizedDistance - minDistance) / (maxDistance - minDistance))
        );

        if (Math.abs(dy) < 40) {
            curveReductionFactor = maxCurveReduction;
        } else if (Math.abs(dy) > 300) {
            curveReductionFactor = minCurveReduction;
        } else {
            const dyFactor = (Math.abs(dy) - 40) / (300 - 40);
            curveReductionFactor = maxCurveReduction - dyFactor * (maxCurveReduction - minCurveReduction);
        }

        const offsetMultiplier = 1 + (offset * 0.5);
        const xMultiplier = isTargetLeft ? -1 : 1;
        const yMultiplier = isTargetTop ? -1 : 1;
        const translation = translateXwise
            ? [xMultiplier * offsetMultiplier * curveReductionFactor, 0]
            : [0, yMultiplier * offsetMultiplier * curveReductionFactor];

        const direction = translateXwise ? (isTargetLeft ? 'left' : 'right') : (isTargetTop ? 'top' : 'bottom');

        const source = {
            //override direction if source and target are the same
            //direction: (sourceCapsule === targetCapsule) ? 'right' : direction,
            direction: direction,
            node: (() => sourceCapsule),
            translation: (sourceCapsule === targetCapsule) ? [-(2 + offset), 1 + offset] : translation
        };

        const target = {
            //override direction if source and target are the same
            //direction: (sourceCapsule === targetCapsule) ? 'left' : direction,
            direction: direction,
            node: (() => targetCapsule),
            translation: (sourceCapsule === targetCapsule) ? [2 + offset, 1 + offset] : translation
        };

        // Move the arrowhead back 3% of arrow length for each arrow between these two entities.
        const adjustedDistance = (100 - 3 * (offset - 1)) / 100;
        // Agreement arrows are double ended.
        const bidirectional = (arrow.type === 'AgreementLink' || arrow.color === '#256F8C')

        const arrowRef = vca.snaps.CreateArrow(source, target, adjustedDistance, bidirectional);

        var arrowSvg: SVGElement = arrowRef.node.firstChild;
        arrowSvg.style.stroke = arrow.color;

        if (arrow.type) {
            arrowSvg.classList.add(arrow.type);
        }

        if (arrow.dashed) {
            arrowSvg.classList.add('dashed');
        }

        // all arrows will have a contrasting white arrow underneath them to fill any dashes (to provide contrast when drawing over regions)
        var contrastArrow = vca.snaps.CreateArrow(source, target, adjustedDistance, bidirectional);
        var contrastSvg = contrastArrow.node.firstChild;
        contrastSvg.classList.add('contrast');

        // add click event listener to the arrow
        arrowSvg.onclick = async (mouseEvent: any) => {
            await this.dotNetObjectReference.invokeMethodAsync('ShowFlowDetailPopup', mouseEvent.pageX, mouseEvent.pageY, linkIds, arrow.sourceId, arrow.targetId);

            // Try to get the popup element and detect if it overflows the window (right and bottom edges)
            const popup = document.getElementById('flow-details-popup');

            if (popup && mouseEvent) {
                var x, y = 0;
                // get the bounding box of the popup
                const popupRect = popup.getBoundingClientRect();

                // Check for right edge overflow
                if ((mouseEvent.x + popupRect.width) > window.innerWidth) {
                    x = window.innerWidth - popupRect.width;
                }
                else {
                    x = mouseEvent.pageX;
                }

                // Check for bottom edge overflow
                if ((mouseEvent.y + popupRect.height) > window.innerHeight) {
                    y = window.innerHeight - popupRect.height;
                }
                else {
                    y = mouseEvent.pageY;
                }

                // Update the popup position
                await this.dotNetObjectReference.invokeMethodAsync('ShowFlowDetailPopup', x, y, linkIds, arrow.sourceId, arrow.targetId);
            }
        }

        document.body.append(...[contrastSvg, arrowSvg]);
    }

    private setFlowTypesToHide(flowTypes: string[]) {
        this.flowTypesToHide = flowTypes;
        this.drawFlows();
    }

    private updateFlowNodesAndLines() {
        // re-draw arrows
        if (!this.isTransitioning) {
            if ((this.mapMode === MapMode.Flows && !this.isVisualizing && !this.isTransitioning) ||
                (this.isVisualizing && this.filteredFlowLinks && this.filteredFlowLinks.length > 0)) {
                this.drawLinksBetweenCapsules();
            }
        }
    }

    public async updateMapVisuals(animate: boolean = false, regenerateIcons = false) {
        //console.debug('updateMapVisuals', animate, regenerateIcons);
        this.clearFlows();
        if (regenerateIcons) {
            this.generateIcons();
        }
        this.drawIcons();
        await this.hidePopups();

        if (this.mapMode === MapMode.Flows || (this.isVisualizing && this.filteredFlowLinks?.length > 0)) {
            this.drawFlows(animate);
        }
    }

    public async setMapMode(mapMode: MapMode) {
        this.mapMode = mapMode;
        this.flowTypesToHide = [];

        if (this.isVisualizing) {
            return;
        }

        await this.updateMapVisuals(false, true);
    }

    public setAllEntities(entities: IEntityDto[]) {
        this.allEntities = new Map<string, IEntityDto[]>();
        entities.forEach(e => {
            this.allEntities.get(e.countryCode)?.push(e) || this.allEntities.set(e.countryCode, [e]);
        });
    }

    public setFilteredEntities(filteredEntities: IEntityDto[]) {
        this.filteredEntities = new Map<string, IEntityDto[]>();
        filteredEntities.forEach(e => {
            this.filteredEntities.get(e.countryCode)?.push(e) || this.filteredEntities.set(e.countryCode, [e]);
        });
    }

    public setCountryColors() {
        this.countries.forEach((countries, regionCode) => {
            countries.forEach(country => {
                const fillColor = this.allEntities.get(country.countryCode) ? this.regions.find(r => r.regionCode === regionCode).color.dark : this.regions.find(r => r.regionCode === regionCode).color.light;
                this.svg.select(`#${country.countryCode}`).attr('fill', fillColor);
            });
        });
    }

    public async setSelectedRegionAndCountry(regionCode: string, countryCode: string) {
        if (this.selectedCountryCode != countryCode ||
            this.selectedRegionCode != regionCode) {

            this.selectedCountryCode = countryCode;
            this.selectedRegionCode = regionCode;

            //this.generateIcons();
            //this.drawIcons();
            
            if (this.selectedCountryCode) {
                await this.transitionToCountry(countryCode);
            } else if (this.selectedRegionCode) {
                await this.transitionToRegion(regionCode);
            } else {
                await this.transitionToGlobal();
            }
        }
    }

    public async transitionToGlobal(transitionDuration: number = 1000) {
        this.selectedCountryCode = null;
        this.selectedRegionCode = null;

        await this.beforeTransition();
        this.hideRegionsExcept(null);
        this.zoomToRegion(this.geoJson.features, false, transitionDuration);
        await this.afterTransition(transitionDuration);
    }

    public async transitionToRegion(regionCode: string, transitionDuration: number = 1000) {
        await this.beforeTransition();
        this.hideRegionsExcept(regionCode);
        // get all countries in the region that have entities
        let regionFeatures = this.geoJson.features.filter(feature => this.countries.get(regionCode).find(c => c.countryCode === feature.properties["ISO_A2"] && this.allEntities.get(c.countryCode)));
        if (regionFeatures.length === 0) {
            // fall back to all countries in the region if none have entities
            regionFeatures = this.geoJson.features.filter(feature => this.countries.get(regionCode).find(c => c.countryCode === feature.properties["ISO_A2"]));
        }
        this.zoomToRegion(regionFeatures, true, transitionDuration);
        await this.afterTransition(transitionDuration);
    }

    public async transitionToCountry(countryCode: string, transitionDuration: number = 1000) {
        await this.beforeTransition();
        this.zoomToRegion([this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode)], false, transitionDuration);
        await this.afterTransition(transitionDuration);
    }

    private async beforeTransition() {
        this.disableAllPointerEvents(true);
        this.disablePanAndZoom();
        this.clearFlows();
        await this.hidePopups();
        // reset stroke width for all country features
        this.regionsGroup.selectAll('path').attr('stroke-width', 1);
        this.isTransitioning = true;
    }

    private async afterTransition(transitionDuration: number = 1000) {
        window.setTimeout(async () => {

            if (!this.isTelestrating) { // might be toggling full screen or resetting zoom while in telestration mode
                if (this.selectedCountryCode || this.selectedRegionCode) {
                    this.disableRegionalMouseEvents();
                    this.enableCountryMouseEvents();
                } else {
                    this.enableRegionalMouseEvents();
                    this.disableCountryMouseEvents();
                }
    
                this.disableAllPointerEvents(false);
                this.enablePanAndZoom();
            }
            
            if (this.mapMode === MapMode.Flows || (this.isVisualizing && this.filteredFlowLinks?.length > 0)) {
                this.drawFlows();
            }
            
            this.isTransitioning = false;
        }, transitionDuration);
    }

    public enableRegionalMouseEvents() {
        //console.debug('enableRegionalMouseEvents');
        this.regions.forEach(region => {
            this.svg.select(`#region-${region.regionCode.replace(' ', '_')}`)
                .attr('cursor', 'pointer')
                .on("mouseover", async () => {
                    await this.onRegionHover(region.regionCode, true);
                })
                .on("mouseout", async () => {
                    await this.onRegionHover(region.regionCode, false);
                })
                .on('click', async () => {
                    await this.onRegionClicked(region.regionCode);
                });
        });
    }
    
    public disableRegionalMouseEvents() {
        //console.debug('disableRegionalMouseEvents');
        this.regions.forEach(region => {
            this.svg.select(`#region-${region.regionCode.replace(' ', '_')}`)
                .attr('cursor', 'unset')
                .on("mouseover", null)
                .on("mouseout", null)
                .on('click', null);
        });
    }

    public enableCountryMouseEvents() {
        //console.debug('enableCountryMouseEvents');
        Array.from(this.countries.values()).flat().forEach(country => {
            if (this.allEntities.get(country.countryCode)) {
                this.svg.select(`path#${country.countryCode}`)
                    .attr('cursor', 'pointer')
                    .on("mouseover", async () => {
                        await this.onCountryHover(country.countryCode, true);
                    })
                    .on("mouseout", async () => {
                        await this.onCountryHover(country.countryCode, false);
                    })
                    .on('click', async () => {
                        await this.onCountryClicked(country.countryCode);
                    });
            }
        });
    }
    
    public disableCountryMouseEvents() {
        //console.debug('disableCountryMouseEvents');
        Array.from(this.countries.values()).flat().forEach(country => {
            this.svg.select(`path#${country.countryCode}`)
                .attr('cursor', 'unset')
                .on("mouseover", null)
                .on("mouseout", null)
                .on('click', null);
        });
    }

    public async onRegionClicked(regionCode: string) {
        if (this.isDragging || this.dragged || this.isTransitioning) {
            return;
        }
        //console.debug(`onRegionClicked: ${regionCode}`);
        await this.dotNetObjectReference.invokeMethodAsync("OnRegionSelectedAsync", regionCode);
    }

    public async onCountryClicked(countryCode: string) {
        if (this.isDragging || this.dragged || this.isTransitioning) {
            return;
        }
        //console.debug(`onCountryClicked: ${countryCode}`);
        await this.dotNetObjectReference.invokeMethodAsync("OnCountrySelectedAsync", countryCode);
    }

    public async onRegionHover(regionCode: string, hovered: boolean) {
        //console.debug(`region hovered ${Date.now()}: ${regionCode} ${hovered}`);
        const regionGroup = this.svg.select(`#region-${regionCode.replace(' ', '_')}`);
        if (hovered) {
            regionGroup.raise();
            this.raiseIcons(this.mapIcons);
            regionGroup.classed("drop-shadow", true);
            this.setRegionColorsOnHover(regionCode);
        } else {
            regionGroup.classed("drop-shadow", false);
            this.setRegionColorsOnHover(null);
        }
    }

    public async onCountryHover(countryCode: string, hovered: boolean) {
        //console.debug(`country hovered ${Date.now()}: ${countryCode} ${hovered}`);
        const countryPath = this.svg.select(`path#${countryCode}`);
        if (hovered) {
            countryPath.raise();
            this.raiseIcons(this.mapIcons);
            this.setCountryColorsOnHover(countryCode);
        }
        else {
            this.setCountryColorsOnHover(null);
        }
    }

    public disableAllPointerEvents(disable: boolean) {
        //console.debug(`Disabling mapContainer and svg pointer events: ${disable}`);
        this.mapContainer.style.pointerEvents = disable ? "none" : "all";
        if (this.svg) {
            this.svg.style("pointer-events", disable ? "none" : "all");
            this.svg.on("click", disable ? null : this.onClick.bind(this));
        }
    }
    
    private enablePanAndZoom() {
        //console.debug('enablePanAndZoom');
        if (this.svg) {
            this.svg.call(this.d3zoom)
                .on("dblclick.zoom", null); // Disable double-click zoom
        }
    }
    
    private disablePanAndZoom() {
        //console.debug('disablePanAndZoom');
        if (this.svg) {
            this.svg.on(".zoom", null);
            this.svg.on(".drag", null);
        }
    }

    public async resizeMap() {
        await this.beforeTransition();
        setTimeout(async () => {
            await this.afterTransition();
        }, 1000);
    }

    public async drawMap(firstDraw: boolean = false, futureStateId: number | null = null) {
        if (firstDraw) {
            this.svg = d3.create('svg')
                .attr("preserveAspectRatio", "xMinYMin meet")
                .attr("viewBox", `0 0 ${this.mapContainer.offsetWidth} ${this.mapContainer.offsetHeight}`)
                .attr("style", "overflow: visible")
                .attr("transform-origin", "0 0")
                .classed("svg-content", true);
            
            this.enablePanAndZoom();

            this.mainGroup = this.svg.append("g").attr("id", "main-group");
            this.regionsGroup = this.mainGroup.append("g").attr("id", "regions-group");
            this.telestrationGroup = this.mainGroup.append("g").attr("id", "telestration-group");
            this.iconsGroup = this.mainGroup.append("g").attr("id", "icons-group");

            // draw regions
            this.regions.forEach(region => {
                const regionCountries = this.countries.get(region.regionCode);
                const regionFeatures = this.geoJson.features.filter(feature => regionCountries.find(c => c.countryCode === feature.properties["ISO_A2"]));

                const regionGroup = this.regionsGroup.append("g")
                    .attr('id', `region-${region.regionCode.replace(' ', '_')}`);

                regionFeatures.forEach(country => {
                    // draw country
                    const countryCode = country.properties["ISO_A2"];
                    const fillColor = this.allEntities.get(countryCode) ? region.color.dark : region.color.light;
                    const countryPath = regionGroup.append("path")
                        .attr("d", this.pathGenerator(country))
                        .attr("id", country.properties["ISO_A2"])
                        .attr('fill', fillColor)
                        .attr('stroke', '#FFFFFF')
                        .attr('stroke-width', 1)
                });
            });
            this.currentScale = 1;
            this.generateIcons();
            this.drawIcons();
            this.mapContainer.replaceChildren(this.svg.node());
            
            //this.zoomToRegion(this.geoJson.features, false)
            //await this.afterTransition();

            this.mutationObserver.observe(this.mainGroup.node(), { attributes: true });
        } else {
            // update country colors, as country presences may be different
            this.setCountryColors();            
        }
        
        // finally, replace any current telestration group with the one for this future state, if it exists (or create a new one)
        //console.debug(`saving telestration group for future state ${this.selectedFutureStateId} to switch to future state ${futureStateId}`);
        this.telestrationGroupsByFutureStateId.set(this.selectedFutureStateId, this.telestrationGroup.node().cloneNode(true));

        this.selectedFutureStateId = futureStateId ?? null;
        this.telestrationGroup.node().replaceChildren(this.telestrationGroupsByFutureStateId.get(this.selectedFutureStateId));

        // also emit an event to notify the "static telestration" (telestration.js)
        // that the future state has changed, so it should load the corresponding
        // svg for the correct future state id.
        const setSvgByIdEvent = new CustomEvent('setSvgById', {
            detail: {
                svgId: this.selectedFutureStateId
            }
        });
        // console.debug('emitting setSvgById event for id: ', this.selectedFutureStateId);
        document.dispatchEvent(setSvgByIdEvent);
    }

    private hideRegionsExcept(regionCode?: string) {
        this.regions.forEach(region => {
            const regionGroup = this.svg.select(`#region-${region.regionCode.replace(' ', '_')}`);
            if (!regionCode) {
                regionGroup.style("display", "block");
            } else if (region.regionCode !== regionCode) {
                regionGroup.style("display", "none");
            }
        });
    }

    private setRegionColorsOnHover(hoveredRegionCode?: string) {
        if (this.svg != null) {
            var regionGroups = this.regionsGroup.selectAll("g[id^='region-']");
            regionGroups.each(function (region) {
                const regionGroup = d3.select(this);
                const regionCode = regionGroup.attr('id').replace('region-', '').replace('_', ' ');

                if (!hoveredRegionCode) {
                    regionGroup.selectAll('path').transition().duration(300).style('fill-opacity', '1').style('line-opacity', '1');
                } else if (regionCode == hoveredRegionCode) {
                    regionGroup.selectAll('path').transition().duration(300).style('fill-opacity', '1').style('line-opacity', '1');
                } else {
                    regionGroup.selectAll('path').transition().duration(300).style('fill-opacity', '.6').style('line-opacity', '.6');
                }
            });
        }
    }

    private setCountryColorsOnHover(hoveredCountryCode?: string | string[]) {
        if (this.svg != null) {
            var countryPaths = this.regionsGroup.selectAll('path');
            countryPaths.each(function (country) {
                const countryPath = d3.select(this);
                const countryCode = countryPath.attr('id');

                if (!hoveredCountryCode) {
                    countryPath.transition().duration(300).style('fill-opacity', '1').style('line-opacity', '1');
                } else if (countryCode === hoveredCountryCode || hoveredCountryCode.includes(countryCode)) {
                    countryPath.transition().duration(300).style('fill-opacity', '1').style('line-opacity', '1');
                } else {
                    countryPath.transition().duration(300).style('fill-opacity', '.6').style('line-opacity', '.6');
                }
            });
        }
    }

    public async setHighlightMode(mode: HighlightFilterMode) {
        this.highlightFilterMode = mode;

        await this.updateMapVisuals(false, true);
    }

    private getEntitiesByHighlightMode(entities: IEntityDto[]): IEntityDto[] {
        if (this.highlightFilterMode === 'Highlighted') {
            return entities.filter(e => this.highlightedEntityIds.includes(e.entityId));
        }

        if (this.highlightFilterMode === 'NotHighlighted') {
            return entities.filter(e => !this.highlightedEntityIds.includes(e.entityId));
        }

        return entities;
    }

    private isEntityVisibleInHighlightMode(entityId: string): boolean {
        if (this.highlightFilterMode === 'Highlighted') {
            return this.highlightedEntityIds.includes(entityId);
        }

        if (this.highlightFilterMode === 'NotHighlighted') {
            return !this.highlightedEntityIds.includes(entityId);
        }

        return true;
    }

    // On individual entity highlight toggle
    public async setHighlightedEntities(highlightedEntityIds?: string[]) {
        this.highlightedEntityIds = highlightedEntityIds ?? [];

        //this.drawIcons();
        await this.updateMapVisuals(false, true);
    }

    public onEntityRowHover(entityId: string) {
        if (this.isTransitioning || this.heroAnimationRunning) {
            return; // ignore hover events while transitioning or hero animation is running
        }
        this.rowHoverEntityId = entityId;
        this.drawIcons();
    }

    public onEntityRowLeave() {
        if (this.isTransitioning || this.heroAnimationRunning) {
            return; // ignore hover events while transitioning or hero animation is running
        }
        this.rowHoverEntityId = null;
        this.drawIcons();
    }

    private zoomToRegion(regionCountries: any[] | undefined, withMargins: boolean | undefined, transitionDuration: number = 1000) {
        this.isTransitioning = true;
        // if regionCountries is undefined, use all countries in the geoJson
        regionCountries ??= this.currentRegionCountries || this.geoJson.features;
        // if withMargins is undefined, use the current zoom margin or false
        withMargins ??= this.currentZoomMargin || false;
        // store the current region countries so they don't need to be passed every time
        this.currentRegionCountries = regionCountries;
        this.currentZoomMargin = withMargins;
        
        // calculate projected region area
        const regionGeometry = { type: "GeometryCollection", geometries: regionCountries.map(country => country.geometry) };
        //const pathGenerator = d3.geoPath(this.projection);
        const bounds = this.pathGenerator.bounds(regionGeometry);

        // calculate the width and height of the map container, adjusted for margins
        const containerWidth = this.mapContainer.clientWidth * (100 - this.leftMarginPercent - this.rightMarginPercent) / 100;
        const containerHeight = this.mapContainer.clientHeight * (100 - this.topMarginPercent - this.bottomMarginPercent) / 100;

        // pad the area with margins if zooming into Region level with only 1 country
        // to prevent Region and Country levels from ever having the same zoom level
        if (withMargins || regionCountries.length == 1) {
            bounds[0][0] -= containerWidth / 10;
            bounds[0][1] -= containerHeight / 10;
            bounds[1][0] += containerWidth / 10;
            bounds[1][1] += containerHeight / 10;
        }
        
        const regionWidth = (bounds[1][0] - bounds[0][0]);
        const regionHeight = (bounds[1][1] - bounds[0][1]);
        
        // Calculate the scale based on the width and height of the map container
        const scale = Math.min(
            containerWidth / regionWidth,
            containerHeight / regionHeight,
            this.maxScale
        );
        
        // Calculate the translate values to center the region in the map container
        const translateX = (containerWidth - regionWidth * scale) / 2 - bounds[0][0] * scale + this.mapContainer.clientWidth * this.leftMarginPercent / 100;
        const translateY = (containerHeight - regionHeight * scale) / 2 - bounds[0][1] * scale + this.mapContainer.clientHeight * this.topMarginPercent / 100;
        
        // Apply the zoom transformation to the SVG
        this.svg.transition()
            .duration(transitionDuration)
            .call(this.d3zoom.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
        
        // Update the current pan and zoom scale for manual panning/zooming
        this.panX = translateX;
        this.panY = translateY;
        this.currentScale = scale;
        // Store the current region transform for resetScale
        this.currentRegionTransform = d3.zoomTransform(this.mainGroup.node());
        
        // Update icons
        this.drawIcons(transitionDuration);
        
        this.isTransitioning = false;
        //console.debug(`translateX: ${translateX}, translateY: ${translateY}, scale: ${scale}`);
    }

    public generateIcons() {
        this.clearEntityIcons();

        var id = 0;

        var entityIconsToDraw = this.getEntitiesByHighlightMode(Array.from(this.filteredEntities.values()).flat());

        Array.from(this.allEntities.values()).flat().forEach(entity => {
            entity.entityTypes.forEach(entityType => {

                var existingIcon = this.mapIcons.find(i => i.entityId === entity.entityId && i.entityType === entityType);

                if (!existingIcon) {
                    this.mapIcons.push({
                        id: id++,
                        regionCode: entity.regionCode,
                        countryCode: entity.countryCode,
                        entityId: entity.entityId,
                        entityType: entityType,
                        visible: entityIconsToDraw.find(e => e.entityId === entity.entityId) != null,
                    });
                }
            });
        });
    }

    private raiseIcons(mapIcons: IEntityIcon[]) {
        // raise capsules to the top of the SVG
        if (this.svg != null) {
            this.svg.selectAll('.icon-capsule').raise();
            // raise icons to the top of the SVG
            mapIcons.forEach(icon => {
                this.svg.select(`#icon${icon.id}`)?.raise();
            });
            // raise circles to the top of the SVG
            mapIcons.forEach(icon => {
                this.svg.select(`#icon${icon.id}-circle`)?.raise();
            });
        }
    }

    public updateIconScaleAndTranslate(icon: IEntityIcon, x: number, y: number, duration: number, visible: boolean, dimmed: boolean) {
        const element = this.svg.select(`#icon${icon.id}`);
        element.style('pointer-events', visible ? 'all' : 'none');

        // if a row hover entity id is set, and this icon is not dimmed, then it needs to be drawn bigger
        var baseScale = (this.rowHoverEntityId && !dimmed) ? 1.2 : 1;

        element.raise().transition().duration(duration)
            .attr("transform", baseScale > 1
                ? `translate(${x - (5 / this.currentScale)},${y - (5 / this.currentScale)}) scale(${baseScale / this.currentScale})`
                : `translate(${x},${y}) scale(${baseScale / this.currentScale})`)
            .style("opacity", visible ? (dimmed ? .3 : 1) : 0);
    }

    private updateFlowDetailsPopupPosition(deltaX: number, deltaY: number) {
        // try to get the popup element to update its position
        const popup = document.getElementById('flow-details-popup');

        if (popup) {
            // get the bounding box of the popup
            const popupRect = popup.getBoundingClientRect();
            var x = popupRect.left + (deltaX / this.currentScale);
            var y = popupRect.top + (deltaY / this.currentScale);

            //x *= this.currentScale;
            //y *= this.currentScale;
            // Check for right edge overflow
            if ((x + popupRect.width) > window.innerWidth) {
                x = window.innerWidth - popupRect.width;
            }

            // Check for bottom edge overflow
            if ((y + popupRect.height) > window.innerHeight) {
                y = window.innerHeight - popupRect.height;
            }

            this.dotNetObjectReference.invokeMethodAsync('ShowFlowDetailPopup', x, y, [], null, null);
        }
    }

    private async hidePopups() {
        if (this.isDragging || this.dragged)
        {
            return;
        }
        //console.debug('hidePopups');
        this.entityPopupIconId = null;
        document.dispatchEvent(new CustomEvent('hideEntityPopup'));
        await this.dotNetObjectReference?.invokeMethodAsync('HideFlowDetailPopup');
    }

    private async getCachedEntityTypeIconMarkup(iconName: string, highlighted: boolean): Promise<string | null> {
        const cacheKey = `${iconName}|${highlighted}`;

        if (this.entityTypeIconMarkupCache.has(cacheKey)) {
            return this.entityTypeIconMarkupCache.get(cacheKey)
        }

        if (!this.dotNetObjectReference) {
            console.warn("CurrentStateMap: dotNetObjectReference is not available; cannot load icon markup.");
            return null;
        }

        const backgroundFillColor = highlighted ? ENTITY_HIGHLIGHT_BACKGROUND : null;

        try {
            const svgMarkup = await this.dotNetObjectReference.invokeMethodAsync(
                "GetIconSvgMarkup",
                "EntityType",
                iconName,
                null,
                backgroundFillColor,
                null) as string | null;
            
            // console.debug(`Retrieved icon markup for icon name '${iconName}' (highlighted: ${highlighted}):`, svgMarkup);

            if (!svgMarkup) {
                console.warn(`CurrentStateMap: icon markup was not returned for icon name '${iconName}'.`);
                return null;
            }

            this.entityTypeIconMarkupCache.set(cacheKey, svgMarkup);
            return svgMarkup;
        } catch (error) {
            console.error("CurrentStateMap: failed to retrieve icon markup via interop.", error);
            return null;
        }
    }

    private drawEntityTypeIcon(id: number, iconSize: number, entityType: string, highlighted: boolean, groupedIconEntityIds?: string[]) {
        const icon = this.mapIcons.find(i => i.id === id);
        if (!icon) {
            return;
        }
        let iconElement = this.svg.select(`#icon${icon.id}`);
        const iconName = entityType;

        const appendIconSvg = () => {
            void this.getCachedEntityTypeIconMarkup(iconName, highlighted).then(markup => {
                if (!markup) {
                    return;
                }

                iconElement.select("svg").remove();
                iconElement.node().insertAdjacentHTML("afterbegin", markup);
                iconElement.select("svg")
                    .attr("width", iconSize)
                    .attr("height", iconSize);

                // raise any circle count element to the top
                iconElement.select('g[id$="-circle"]')?.raise();
            });
        };

        if (iconElement.empty()) {
            // only draw if not already drawn
            iconElement = this.iconsGroup.append("g")
                .attr("id", `icon${icon.id}`)
                .attr("width", iconSize)
                .attr("height", iconSize)
                .style("cursor", "pointer");

            iconElement.append("title")
                .text(entityType ?? "");

            iconElement.node().dataset.iconName = iconName;
            iconElement.node().dataset.highlighted = highlighted;
            appendIconSvg();
        }
        else if (iconElement.node().dataset.iconName !== iconName || iconElement.node().dataset.highlighted !== highlighted) {
            // or replace the icon if the name has changed or highlighting was toggled
            iconElement.node().dataset.iconName = iconName;
            iconElement.node().dataset.highlighted = highlighted;
            iconElement.select("svg").remove();
            appendIconSvg();
        }

        const iconEntityIds = groupedIconEntityIds ?? [icon.entityId];

        iconElement.on("mouseover", () => {
            this.handleEntityIconHover(iconEntityIds, true);
        })
            .on("mouseleave", () => {
                this.handleEntityIconHover(iconEntityIds, false);
            })
            .on("click", (event: MouseEvent) => {
                this.handleIconClick(event, iconEntityIds);
            });
    }

    // Shows entity(s) info in a popup
    private handleIconClick(event: MouseEvent, entityIds: string[]) {
        if (this.isDragging || this.dragged || this.isTransitioning) {
            return;
        }
        event.stopImmediatePropagation();
        
        const iconElement = event.currentTarget as SVGGElement;
        // Store the id to update the popups position when map resizes/pans
        this.entityPopupIconId = iconElement.id;

        const iconPosition = iconElement.getBoundingClientRect();
        let left = iconPosition.right + window.scrollX;
        let top = iconPosition.top + window.scrollY;

        // Event to pass entities & initial coordinates to parent of popup component
        const clickEvent = new CustomEvent('entityIconClick', {
            detail: {
                entityId: entityIds,
                xPosition: left,
                yPosition: top
            }
        });
        document.dispatchEvent(clickEvent);
        
        // wait for the popup to be rendered before updating its position
        setTimeout(() => {
            const entityPopup = document.getElementById('entity-info-popup');
            if (entityPopup) {
                // update the position to fix any collisions caused by initial height/width
                this.updateEntityPopupPosition();
                // add a listener to update the popup position if its height changes
                this.entityPopupResizeObserver.observe(entityPopup);
            }
        }, 100);
    }
    
    private updateEntityPopupPosition() {
        if (!this.entityPopupIconId) {
            return;
        }
        const iconElement = this.svg.select(`#${this.entityPopupIconId}`);
        if (iconElement.empty()) {
            // if the icon element is not found, reset the popup icon id
            console.warn(`Icon element with id ${this.entityPopupIconId} not found, cancelling popup position update`);
            this.entityPopupIconId = null;
            return;
        }
        const entityPopup = document.getElementById('entity-info-popup');
        if (!entityPopup) {
            // if the popup is not found, reset the popup icon id
            console.warn('entity-info-popup not found, cancelling popup position update');
            this.entityPopupIconId = null;
            return;
        }
        
        //console.debug(`updateEntityPopupPosition: ${this.entityPopupIconId}`);
        const iconPosition = iconElement.node().getBoundingClientRect();
        const footerHeight = document.querySelector('footer')?.clientHeight || 0;
        let left = iconPosition.right + window.scrollX;
        let top = iconPosition.top + window.scrollY;
        // Check for right edge overflow
        if ((left + entityPopup.clientWidth) > window.innerWidth) {
            left = iconPosition.left + window.scrollX - entityPopup.clientWidth;
        }
        // Check for bottom edge overflow
        if (top + entityPopup.clientHeight > window.innerHeight - footerHeight) {
            top -= top + entityPopup.clientHeight - (window.innerHeight - footerHeight);
        }
        
        const entityPopupPositionChangedEvent = new CustomEvent('entityPopupPositionChanged', {
            detail: {
                xPosition: left,
                yPosition: top
            }
        });
        document.dispatchEvent(entityPopupPositionChangedEvent);
        entityPopup.style.visibility = 'visible';
        entityPopup.style.opacity = '1';
    }

    private handleEntityIconHover(entityId: string[], isHovered: boolean) {
        if (!entityId.length) {
            return;
        }

        entityId.forEach(id => {
            const entityTableCells = document.querySelectorAll(`[data-row-id='${id}']`) as NodeListOf<HTMLTableCellElement>;
            const entityTableRow = entityTableCells[0]?.parentElement as HTMLElement;
            if (entityTableRow != null) {
                if (isHovered) {
                    entityTableRow.classList.add('icon-hovered');
                }
                else {
                    entityTableRow.classList.remove('icon-hovered');
                }
            }
        })
    }

    private drawIconGroupCount(iconId: number, iconSize: number, groupCount: number) {
        if (this.heroAnimationRunning) {
            // never draw these counts during hero animation
            return;
        }
        var iconElement = this.svg.select(`#icon${iconId}`);
        var circleElement = iconElement.append("g")
            .attr("id", `icon${iconId}-circle`);

        circleElement.append("circle")
            .attr("r", iconSize / 4)
            .attr("fill", "white")
            .attr("stroke", "black")
            .attr("stroke-width", 1);

        circleElement.append("text")
            .attr("fill", "black")
            .attr("font-size", iconSize / 4)
            .attr("font-weight", "bold")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "central")
            .text(groupCount);

        circleElement.attr("transform", `translate(${(iconSize * .85)},${(iconSize * .15)})`);
    }

    private drawCountryLevelIcons(countryCode: string, transitionDuration: number, visible: boolean) {
        //var mapNode = document.getElementById(this.mapNodeName);
        var rootFontSize = parseFloat(getComputedStyle(this.mapContainer).fontSize);
        var iconSize = rootFontSize * 2;
        var countryFeature = this.geoJson.features.find(feature => feature.properties["ISO_A2"] === countryCode);
        if (!countryFeature) {
            return;
        }

        // check the country area to determine if it is smaller than the icon area
        const countryArea = this.pathGenerator.area(countryFeature);
        const countryBounds = this.pathGenerator.bounds(countryFeature);
        const countryCenter = this.pathGenerator.centroid(countryFeature.geometry);

        // if country is small, remove the stroke width to help make be visible
        var countryPath = this.svg.select(`#${countryCode}`);
        countryPath.attr('stroke-width', countryArea < 5 ? 0 : 1);
        if (countryArea < 5) {
            countryPath.raise();
        }
        // and move the icons down by the height of one icon
        var yOffset = countryArea < 5 ? rootFontSize / this.currentScale : 0;
        countryCenter[1] += yOffset;

        var countryIcons = this.mapIcons.filter(icon => icon.countryCode === countryCode && icon.visible);

        // group countryIcons by entityType
        var groupedByEntityType = countryIcons.reduce((acc, icon) => acc.set(icon.entityType.name, [...(acc.get(icon.entityType.name) || []), icon]), new Map<string, IEntityIcon[]>());
        var i = 0;

        groupedByEntityType.forEach((entityTypeGrouping) => {
            // offset on y-axis by the number of entities already drawn for this country
            var xOffset = ((-iconSize / 2) * Math.min(groupedByEntityType.size, 4)) / this.currentScale;

            if (i === 0) {// && visible) {
                // if this is the first grouping, draw a 'capsule' to group all of this country's icons
                var x = countryCenter[0] + xOffset;
                var y = countryCenter[1];
                var capsuleWidth = (iconSize * Math.min(groupedByEntityType.size, 4)) + 10;
                var capsuleHeight = (iconSize * Math.ceil(groupedByEntityType.size / 4)) + 8;

                this.iconsGroup.append("g")
                    .attr("id", `capsule${countryCode}`)
                    .classed('icon-capsule', true)
                    .style("visibility", visible ? 'visible' : 'hidden')
                    .style("opacity", 0)
                    .attr("transform", `translate(${x - (5 / this.currentScale)},${y - (5 / this.currentScale)}) scale(${1 / this.currentScale})`)
                    .append("rect")
                    .attr("fill", 'white')
                    .attr("width", capsuleWidth)
                    .attr("height", capsuleHeight)
                    .attr("rx", (iconSize + 8) / 2)
                    .attr("ry", (iconSize + 8) / 2)
            }

            // for each grouping of icons, draw them on top of each other (to simulate a single icon)
            var drawingFirstInGroup = true;
            var numberInFilteredEntities = entityTypeGrouping.filter(icon => this.filteredEntities.get(countryCode)?.find(e => e.entityId === icon.entityId)).length;
            var numberOfVisibleByHighlightMode = entityTypeGrouping.filter(icon => this.isEntityVisibleInHighlightMode(icon.entityId)).length;

            const isAnyInFilteredEntities = numberInFilteredEntities > 0;
            const isAnyHighlighted = entityTypeGrouping.some(icon => this.highlightedEntityIds.includes(icon.entityId));
            const isAnyVisibleByHighlightMode = numberOfVisibleByHighlightMode > 0;

            const groupedIconEntityIds = entityTypeGrouping.map(icon => icon.entityId);

            entityTypeGrouping.forEach(icon => {
                this.drawEntityTypeIcon(icon.id, iconSize, entityTypeGrouping[0].entityType.name, isAnyHighlighted, groupedIconEntityIds);

                var dimmed = false;
                if (visible && drawingFirstInGroup) {
                    // append a white circle to indicate multiple icons
                    // the circle should contain a number indicating the count of entities

                    if (this.highlightFilterMode !== 'All' && numberOfVisibleByHighlightMode > 1) {
                        this.drawIconGroupCount(icon.id, iconSize, numberOfVisibleByHighlightMode);
                    }
                    else if (this.highlightFilterMode === 'All' && entityTypeGrouping.length > 1) {
                        this.drawIconGroupCount(icon.id, iconSize, entityTypeGrouping.length);
                    }

                    //set dimmmed if rowHoverEntityId is set but it is not one of these entities
                    if (this.rowHoverEntityId && !entityTypeGrouping.find(icon => this.rowHoverEntityId == icon.entityId) ||
                        this.visualizationEntityIds && !entityTypeGrouping.find(icon => this.visualizationEntityIds.includes(icon.entityId))) {
                        dimmed = true;
                    }
                }

                // position the icon based on the number of groupings already drawn, centered on the country center
                var x = (countryCenter[0] + xOffset + (iconSize * (i % 4) / this.currentScale));
                var y = (countryCenter[1] + (iconSize * Math.floor(i / 4) / this.currentScale));

                //override visible if highlight mode is ON but none of these entities are highlighted
                var visibleOverride = visible && drawingFirstInGroup && isAnyInFilteredEntities && (this.highlightFilterMode === 'All' || isAnyVisibleByHighlightMode);
                this.updateIconScaleAndTranslate(icon, x, y, transitionDuration, visibleOverride, dimmed);

                drawingFirstInGroup = false;
            });

            i++;
        });
    }

    private drawRegionLevelIcons(region: IRegion, transitionDuration: number, visible: boolean) {
        var mapNode = document.getElementById(this.mapNodeName);
        var rootFontSize = parseFloat(getComputedStyle(mapNode).fontSize);
        var iconSize = rootFontSize * 2;
        var regionCountries = this.countries.get(region.regionCode);
        regionCountries.forEach(country => {
            var countryFeature = this.geoJson.features.find(feature => feature.properties["ISO_A2"] === country.countryCode);
            if (!countryFeature) {
                return;
            }

            // check the country area to determine if it is smaller than the icon area
            const countryArea = this.pathGenerator.area(countryFeature.geometry);
            const countryBounds = this.pathGenerator.bounds(countryFeature);
            const countryCenter = this.pathGenerator.centroid(countryFeature.geometry);

            // if country is small, remove the stroke width to help make be visible
            const countryPath = this.svg.select(`#${country.countryCode}`);
            countryPath.attr('stroke-width', countryArea < 5 ? 0 : 1);
            if (countryArea < 5) {
                countryPath.raise();
            }
            // and move the icons down by the height of one icon
            var yOffset = countryArea < 5 ? rootFontSize / this.currentScale : 0;
            countryCenter[1] += yOffset;

            var countryIcons = this.mapIcons.filter(icon => icon.countryCode === country.countryCode && icon.visible);

            // group countryIcons by entityType
            var groupedByEntityType = countryIcons.reduce((acc, icon) => acc.set(icon.entityType.name, [...(acc.get(icon.entityType.name) || []), icon]), new Map<string, IEntityIcon[]>());
            var i = 0;
            groupedByEntityType.forEach(entityTypeGrouping => {
                // to center the icons on the country center, calculate the xOffset based on the number of icons in the group
                var xOffset = ((-iconSize / 2) * Math.min(groupedByEntityType.size, 4)) / this.currentScale;

                if (i === 0) {// && visible) {
                    // if this is the first grouping, draw a 'capsule' to group all of this country's icons
                    var x = countryCenter[0] + xOffset;
                    var y = countryCenter[1];
                    var capsuleWidth = (iconSize * Math.min(groupedByEntityType.size, 4)) + 10;
                    var capsuleHeight = (iconSize * Math.ceil(groupedByEntityType.size / 4)) + 8;

                    this.iconsGroup.append("g")
                        .attr("id", `capsule${country.countryCode}`)
                        .classed('icon-capsule', true)
                        .style("visibility", visible ? 'visible' : 'hidden')
                        .style("opacity", 0)
                        .attr("transform", `translate(${x - (5 / this.currentScale)},${y - (5 / this.currentScale)}) scale(${1 / this.currentScale})`)
                        .append("rect")
                        .attr("fill", 'white')
                        .attr("width", capsuleWidth)
                        .attr("height", capsuleHeight)
                        .attr("rx", (iconSize + 8) / 2)
                        .attr("ry", (iconSize + 8) / 2)
                }

                // for each grouping of icons, draw them on top of each other (to simulate a single icon)
                var drawingFirstInGroup = true;
                var numberInFilteredEntities = entityTypeGrouping.filter(icon => this.filteredEntities.get(country.countryCode)?.find(e => e.entityId === icon.entityId)).length;
                var numberOfVisibleByHighlightMode = entityTypeGrouping.filter(icon => this.isEntityVisibleInHighlightMode(icon.entityId)).length;

                const isAnyInFilteredEntities = numberInFilteredEntities > 0;
                const isAnyHighlighted = entityTypeGrouping.some(icon => this.highlightedEntityIds.includes(icon.entityId));
                const isAnyVisibleByHighlightMode = numberOfVisibleByHighlightMode > 0;

                const groupedIconEntityIds = entityTypeGrouping.map(icon => icon.entityId);

                entityTypeGrouping.forEach(icon => {
                    this.drawEntityTypeIcon(icon.id, iconSize, entityTypeGrouping[0].entityType.name, isAnyHighlighted, groupedIconEntityIds);

                    var dimmed = false;
                    if (visible && drawingFirstInGroup) {
                        // append a white circle to indicate multiple icons
                        // the circle should contain a number indicating the count of entities
                        if (this.highlightFilterMode !== 'All' && numberOfVisibleByHighlightMode > 1) {
                            this.drawIconGroupCount(icon.id, iconSize, numberOfVisibleByHighlightMode);
                        }
                        else if (this.highlightFilterMode === 'All' && entityTypeGrouping.length > 1) {
                            this.drawIconGroupCount(icon.id, iconSize, entityTypeGrouping.length);
                        }

                        //set dimmmed if rowHoverEntityId is set but it is not one of these entities
                        if (this.rowHoverEntityId && !entityTypeGrouping.find(icon => this.rowHoverEntityId == icon.entityId) ||
                            this.visualizationEntityIds && !entityTypeGrouping.find(icon => this.visualizationEntityIds.includes(icon.entityId))) {
                            dimmed = true;
                        }
                    }

                    // position the icon based on the number of groupings already drawn, centered on the country center
                    var x = (countryCenter[0] + xOffset + (iconSize * (i % 4) / this.currentScale));
                    var y = (countryCenter[1] + (iconSize * Math.floor(i / 4) / this.currentScale));

                    //override visible if highlight mode is ON but none of these entities are highlighted
                    var visibleOverride = visible && drawingFirstInGroup && isAnyInFilteredEntities && (this.highlightFilterMode === 'All' || isAnyVisibleByHighlightMode);
                    this.updateIconScaleAndTranslate(icon, x, y, transitionDuration, visibleOverride, dimmed);

                    drawingFirstInGroup = false;
                });

                i++;
            });
        });
    }

    private drawGlobalLevelIcons(transitionDuration: number, visible: boolean) {
        var mapNode = document.getElementById(this.mapNodeName);
        if (!mapNode) {
            return;
        }
        var rootFontSize = parseFloat(getComputedStyle(mapNode).fontSize);
        var iconSize = rootFontSize * 2;
        this.regions.forEach(region => {
            var regionFeatures = this.geoJson.features.filter(feature => this.countries.get(region.regionCode).find(c => c.countryCode === feature.properties["ISO_A2"]))
            var countriesWithPresences = this.countries.get(region.regionCode).filter(c => this.allEntities.get(c.countryCode));
            var featuresWithPresences = this.geoJson.features.filter(feature => countriesWithPresences.find(c => c.countryCode === feature.properties["ISO_A2"]));

            const regionGeometry = { type: "GeometryCollection", geometries: featuresWithPresences.map(f => f.geometry) };
            const centerOfPresences = this.pathGenerator.centroid(regionGeometry);

            regionGeometry.geometries = regionFeatures.map(f => f.geometry);
            const centerOfRegion = this.pathGenerator.centroid(regionGeometry);

            // average the center of presences and the center of the region to get the location for drawing icons
            const center = [(centerOfPresences[0] + centerOfRegion[0]) / 2, (centerOfPresences[1] + centerOfRegion[1]) / 2];

            var regionIcons = this.mapIcons.filter(icon => icon.regionCode === region.regionCode && icon.visible);

            var groupedByEntityType = regionIcons.reduce((acc, icon) => acc.set(icon.entityType.name, [...(acc.get(icon.entityType.name) || []), icon]), new Map<string, IEntityIcon[]>());
            var i = 0;
            groupedByEntityType.forEach(entityTypeGrouping => {
                // to center multiple groupings on a region, shift them to the left according to how many groupings there are
                var xOffset = ((-iconSize / 2) * Math.min(groupedByEntityType.size, 5)) / this.currentScale;

                if (i === 0) {// && visible) {
                    // if this is the first grouping, draw a 'capsule' to group all of this region's icons
                    var x = center[0] + xOffset;
                    var y = center[1];
                    var capsuleWidth = (iconSize * Math.min(groupedByEntityType.size, 5)) + 10;
                    var capsuleHeight = (iconSize * Math.ceil(groupedByEntityType.size / 5)) + 10;

                    this.iconsGroup.append("g")
                        .attr("id", `capsule${region.regionCode}`)
                        .classed('icon-capsule', true)
                        .style("visibility", visible ? 'visible' : 'hidden')
                        .style("opacity", 0)
                        .attr("transform", `translate(${x - (5 / this.currentScale)},${y - (5 / this.currentScale)}) scale(${1 / this.currentScale})`)
                        .append("rect")
                        .attr("fill", 'white')
                        .attr("width", capsuleWidth)
                        .attr("height", capsuleHeight)
                        .attr("rx", (iconSize + 8) / 2)
                        .attr("ry", (iconSize + 8) / 2)
                }

                // for each grouping of icons, draw them on top of each other (to simulate a single icon)
                var drawingFirstInGroup = true;
                var numberInFilteredEntities = entityTypeGrouping.filter(icon => Array.from(this.filteredEntities.values()).flat().find(e => e.entityId === icon.entityId)).length;
                var numberOfVisibleByHighlightMode = entityTypeGrouping.filter(icon => this.isEntityVisibleInHighlightMode(icon.entityId)).length;

                const isAnyInFilteredEntities = numberInFilteredEntities > 0;
                const isAnyHighlighted = entityTypeGrouping.some(icon => this.highlightedEntityIds.includes(icon.entityId));
                const isAnyVisibleByHighlightMode = numberOfVisibleByHighlightMode > 0;

                const groupedIconEntityIds = entityTypeGrouping.map(icon => icon.entityId);

                entityTypeGrouping.forEach(icon => {
                    this.drawEntityTypeIcon(icon.id, iconSize, entityTypeGrouping[0].entityType.name, isAnyHighlighted, groupedIconEntityIds);

                    var dimmed = false;
                    if (visible && drawingFirstInGroup) {
                        // append a white circle to indicate multiple icons
                        // the circle should contain a number indicating the count of entities
                        if (this.highlightFilterMode !== 'All' && numberOfVisibleByHighlightMode > 1) {
                            this.drawIconGroupCount(icon.id, iconSize, numberOfVisibleByHighlightMode);
                        }
                        else if (this.highlightFilterMode === 'All' && entityTypeGrouping.length > 1) {
                            this.drawIconGroupCount(icon.id, iconSize, entityTypeGrouping.length);
                        }

                        //set dimmmed if rowHoverEntityId is set but it is not one of these entities
                        if (this.rowHoverEntityId && !entityTypeGrouping.find(icon => this.rowHoverEntityId == icon.entityId) ||
                            this.visualizationEntityIds && !entityTypeGrouping.find(icon => this.visualizationEntityIds.includes(icon.entityId))) {
                            dimmed = true;
                        }
                    }

                    // position the icon based on the number of groupings already drawn, centered on the country center
                    var x = (center[0] + xOffset + (iconSize * (i % 5) / this.currentScale));
                    var y = (center[1] + (iconSize * Math.floor(i / 5) / this.currentScale));

                    //override visible if highlight mode is ON but none of these entities are highlighted
                    var visibleOverride = visible && drawingFirstInGroup && isAnyInFilteredEntities && (this.highlightFilterMode === 'All' || isAnyVisibleByHighlightMode);
                    this.updateIconScaleAndTranslate(icon, x, y, transitionDuration, visibleOverride, dimmed);

                    drawingFirstInGroup = false;
                });

                i++;
            });
        });

    }

    private drawIcons(transitionDuration: number = 0) {
        if (this.svg != null) {
            let animate = transitionDuration > 0;
            
            // clear any circles that were drawn to indicate multiple icons
            this.svg.selectAll('g[id$="-circle"]')?.remove();

            // clear any capsules that were drawn to group entity icons
            this.svg.selectAll('.icon-capsule')?.remove();

            // update visible property on all map icons
            const iconsToBeVisble = this.heroAnimationRunning 
                ? this.selectedRegionCode ? Array.from(this.allEntities.values()).flat().filter(e => e.regionCode === this.selectedRegionCode) : Array.from(this.allEntities.values()).flat()
                : this.getEntitiesByHighlightMode(Array.from(this.filteredEntities.values()).flat());
            this.mapIcons.forEach(icon => {
                icon.visible = iconsToBeVisble.some(e => e.entityId === icon.entityId);
                if (!icon.visible) {
                    // hide the icon if it should not be visible
                    this.svg.select(`#icon${icon.id}`).transition().duration(transitionDuration / 2).style("opacity", 0);
                }
            });

            if (this.isVisualizing) {
                // Draw icons at country level, grouped by entity id
                Array.from(this.countries.values()).flat().forEach(country => {
                    // Icons should be visible only in the selected country
                    var visible = this.countriesForVisualization.includes(country.countryCode);
                    this.drawCountryLevelIcons(country.countryCode, transitionDuration, visible);
                });
            } else if (this.selectedCountryCode) {
                // Draw icons at country level, grouped by entity id
                Array.from(this.countries.values()).flat().forEach(country => {
                    // Icons should be visible only in the selected country
                    var visible = this.selectedCountryCode === country.countryCode;
                    this.drawCountryLevelIcons(country.countryCode, transitionDuration, visible);
                });

            } else if (this.selectedRegionCode) {
                // Draw icons at regional level, centered on their respective country
                this.regions.forEach(region => {
                    // Icons should be visible only in the selected region
                    var visible = this.selectedRegionCode === region.regionCode;
                    this.drawRegionLevelIcons(region, transitionDuration, visible);
                });

            } else {
                // Draw icons grouped by region, centered on their respective region
                // All icons should be visible at the global level
                this.drawGlobalLevelIcons(transitionDuration, true);
            }

            // fade in any capsules that were drawn, unless this is part of the hero animation
            if (!this.heroAnimationRunning){
                this.svg.selectAll('.icon-capsule')?.transition().delay(animate ? 500 : 0).duration(transitionDuration).style("opacity", 1);
            }
        }
    }

    public clearEntityIcons() {
        if (this.svg) {
            // clear any circles that were drawn to indicate multiple icons
            this.svg.selectAll('g[id$="-circle"]')?.remove();

            // clear any capsules that were drawn to group entity icons
            this.svg.selectAll('.icon-capsule')?.remove();

            // remove all icons
            this.mapIcons.forEach(icon => {
                this.svg.select(`#icon${icon.id}`)?.remove();
            });
        }

        this.mapIcons.length = 0;
    }

    // Called from C#
    public updateMargins(leftPercent: number, topPercent: number, rightPercent: number, bottomPercent: number) {
        // Save the new margins for future zooming in and out of regions
        this.leftMarginPercent = leftPercent;
        this.topMarginPercent = topPercent;
        this.rightMarginPercent = rightPercent;
        this.bottomMarginPercent = bottomPercent;
        
        this.zoomToRegion(null, null);
    }

    public async resetScale() {
        this.zoomToRegion(null, null);
    }

    public cleanupMap() {
        const mapContainer = document.getElementById(this.mapNodeName);

        // Unobserve map
        // if (this.resizeObserver) {
        //     this.resizeObserver.unobserve(mapContainer);
        //     this.resizeObserver.disconnect();
        //     this.resizeObserver = null;
        // }

        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }

        this.clearFlows();

        // Remove the SVG from DOM
        if (this.svg) {
            this.svg.remove();
        }

        // Remove the map container from DOM
        if (mapContainer) {
            mapContainer.remove();
        }
        
        // Emit an event to dispose of Static Telestration (telestration.js) which otherwise 
        // would not know that the map is being disposed.
        const disposeEvent = new CustomEvent('disposeStaticTelestration');
        document.dispatchEvent(disposeEvent);
    }
}

export function createCurrentStateMap(mapElementId: string, maxScale: number, marginLeftPercent: number, marginTopPercent: number, marginRightPercent: number, marginBottomPercent: number, dotNetHelper: any): CurrentStateMap {
    try {
        const map = new CurrentStateMap(mapElementId, maxScale, marginLeftPercent, marginTopPercent, marginRightPercent, marginBottomPercent, dotNetHelper);
        return map;
    } catch (error) {
        console.error("Error occurred while creating CurrentStateMap: ", error);
        throw error;
    }
}

// https://stackoverflow.com/a/73683597
const mapsAreEqual = (m1, m2) => m1.size === m2.size && Array.from(m1.keys()).every((key) => m1.get(key) === m2.get(key));

const arraysAreEqual = (a1, a2) => a1.length === a2.length && a1.every((val, index) => val === a2[index]);
